import React, { useState, useEffect } from 'react';
import { 
  ShieldAlert, 
  CheckCircle2, 
  Lock, 
  Plus, 
  Building2, 
  Users, 
  Layers, 
  Settings, 
  DollarSign, 
  Check, 
  Calendar, 
  ArrowRight, 
  Bot, 
  Sparkles, 
  CheckSquare, 
  TrendingUp, 
  Coins, 
  MessageSquare, 
  PlusCircle, 
  RefreshCw, 
  Eye,
  Info,
  LogOut,
  LayoutDashboard,
  BarChart3,
  Filter,
  Download,
  Pencil,
  Trash2,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import RazorpayModal from '../../components/RazorpayModal';
import PayPalModal from '../../components/PayPalModal';


import api from '../../utils/api';

export default function SaasPortal() {
  const { user, logout } = useAuth();
  const [activeRole, setActiveRole] = useState('distributor');
  const [isRazorpayOpen, setIsRazorpayOpen] = useState(false);
  const [isPayPalOpen, setIsPayPalOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('razorpay'); // 'razorpay' | 'paypal'

  // Sub-tab selectors
  const [saTab, setSaTab] = useState('overview');
  const [distTab, setDistTab] = useState('overview');
  const [showClientProfile, setShowClientProfile] = useState(false);

  // Filters
  const [planFilter, setPlanFilter] = useState('all');
  const [distributorFilter, setDistributorFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // API-driven state
  const [distributors, setDistributors] = useState([]);
  const [clients, setClients] = useState([]);
  const [logs, setLogs] = useState([]);
  const [plans, setPlans] = useState({});
  const [dataLoading, setDataLoading] = useState(true);

  // Derived canonical PLANS mapping used throughout the component (fallbacks for offline/default)
  const PLANS = Object.keys(plans).length > 0
    ? Object.fromEntries(
        Object.entries(plans).map(([planKey, plan]) => {
          const allowedFeatures = Array.isArray(plan.allowedFeatures) ? plan.allowedFeatures : [];
          const features = Array.isArray(plan.features) ? plan.features : [];
          return [
            planKey,
            {
              ...plan,
              allowedFeatures,
              features,
              locked: ['Dashboard', 'Jobs', 'Customers', 'Products', 'Inventory', 'Expenses', 'Statement', 'Reports', 'Settings']
                .filter((module) => !allowedFeatures.includes(module)),
            },
          ];
        })
      )
    : {
        Starter: {
          name: 'Starter',
          price: 49,
          cycle: 'Monthly',
          description: 'Perfect for small, single-location repair counters.',
          allowedFeatures: ['Dashboard', 'Jobs', 'Customers', 'Settings'],
          features: [
            'Basic Repair Job Management',
            'Up to 2 Branch Locations',
            'Up to 100 Repair Jobs/month',
            'Standard Thermal Receipts & PDF Invoice printing',
          ],
          locked: ['Products', 'Inventory', 'Expenses', 'Statement', 'Reports'],
        },
        Growth: {
          name: 'Growth',
          price: 99,
          cycle: 'Monthly',
          description: 'Great for growing repair shops with multiple branches.',
          allowedFeatures: ['Dashboard', 'Jobs', 'Customers', 'Products', 'Inventory', 'Expenses', 'Statement', 'Settings'],
          features: [
            'Basic Repair Job Management',
            'Unlimited Branch Locations',
            'Unlimited Repair Jobs',
            'Advanced Inventory Control',
            'Expense Tracking & Branch Reports',
            'Customer Ledger & Account Statements',
            'Standard Reports',
          ],
          locked: ['Reports'],
        },
        Enterprise: {
          name: 'Enterprise',
          price: 249,
          cycle: 'Monthly',
          description: 'For large franchises needing deep automation and control.',
          allowedFeatures: ['Dashboard', 'Jobs', 'Customers', 'Products', 'Inventory', 'Expenses', 'Statement', 'Reports', 'Settings'],
          features: [
            'All Growth features',
            'Full Business Analytics & PDF Report Exports',
            'Google Drive Premium Sync',
            'Custom Branding & Barcode Calibration',
            'Dedicated Account Manager & 24/7 Priority SLA support',
          ],
          locked: [],
        },
      };

  // Fetch all SaaS data from the backend
  const fetchAll = async () => {
    try {
      setDataLoading(true);
      const [plansRes, clientsRes, logsRes, distsRes] = await Promise.all([
        api.get('/api/saas/plans'),
        api.get('/api/saas/clients').catch(() => ({ data: { success: true, data: [] } })),
        api.get('/api/saas/logs').catch(() => ({ data: { success: true, data: [] } })),
        api.get('/api/saas/distributors').catch(() => ({ data: { success: true, data: [] } })),
      ]);

      if (plansRes.data.success) {
        // Convert array to keyed object for compatibility with existing plan UI
        const plansObj = {};
        plansRes.data.data.forEach(p => { plansObj[p.name] = { ...p, id: p.id }; });
        setPlans(plansObj);
      }
      if (clientsRes.data.success) setClients(clientsRes.data.data);
      if (logsRes.data.success) setLogs(logsRes.data.data);
      if (distsRes.data.success) setDistributors(distsRes.data.data);
    } catch (err) {
      console.error('Failed to load SaaS data:', err);
      toast.error('Failed to load portal data');
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Dynamic Custom Plan Action States
  const [newPlanName, setNewPlanName] = useState('');
  const [newPlanPrice, setNewPlanPrice] = useState(49);
  const [newPlanCycle, setNewPlanCycle] = useState('Monthly');
  const [newPlanDesc, setNewPlanDesc] = useState('');
  const [newPlanFeatures, setNewPlanFeatures] = useState(['Dashboard', 'Jobs', 'Customers']);
  const [newPlanBranchLimit, setNewPlanBranchLimit] = useState('unlimited'); // 'unlimited' | number string e.g. '2'
  const [newPlanAdditionalBranchPrice, setNewPlanAdditionalBranchPrice] = useState(99);
  const [editingPlanKey, setEditingPlanKey] = useState(null);

  // Sync to localStorage
  useEffect(() => {
    localStorage.setItem('saas_distributors', JSON.stringify(distributors));
  }, [distributors]);

  useEffect(() => {
    localStorage.setItem('saas_clients', JSON.stringify(clients));
  }, [clients]);

  useEffect(() => {
    localStorage.setItem('saas_logs', JSON.stringify(logs));
  }, [logs]);

  const [selectedDistributorId, setSelectedDistributorId] = useState(distributors[0]?.id || 'dist-1');
  const [selectedClientId, setSelectedClientId] = useState(clients[0]?.id || 'client-1');

  // Synchronize active view role and impersonated entities based on logged-in SaaS credentials
  useEffect(() => {
    if (user) {
      if (user.role === 'SUPER_ADMIN') {
        setActiveRole('superadmin');
      } else if (user.role === 'DISTRIBUTOR') {
        setActiveRole('distributor');
        if (user.distributorId) {
          setSelectedDistributorId(user.distributorId);
        }
      } else if (user.role === 'CLIENT') {
        setActiveRole('client');
        if (user.clientId) {
          setSelectedClientId(user.clientId);
        }
      }
    }
  }, [user]);


  // Super Admin Action States
  const [newDistName, setNewDistName] = useState('');
  const [newDistEmail, setNewDistEmail] = useState('');
  const [newDistRegion, setNewDistRegion] = useState('Global');
  const [newDistMaxClients, setNewDistMaxClients] = useState(5);
  const [newDistUsername, setNewDistUsername] = useState('');
  const [newDistPassword, setNewDistPassword] = useState('');

  // Distributor Action States
  const [newClientName, setNewClientName] = useState('');
  const [newClientShop, setNewClientShop] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientAddress, setNewClientAddress] = useState('');
  const [newClientCity, setNewClientCity] = useState('');
  const [newClientState, setNewClientState] = useState('');
  const [newClientPincode, setNewClientPincode] = useState('');
  const [newClientWebsite, setNewClientWebsite] = useState('');
  const [newClientWhatsapp, setNewClientWhatsapp] = useState('');
  const [newClientGst, setNewClientGst] = useState('');
  const [newClientDoc, setNewClientDoc] = useState(null);

  // Super Admin specific states for Client management
  const [editingClientId, setEditingClientId] = useState(null);
  const [newClientUsername, setNewClientUsername] = useState('');
  const [newClientPassword, setNewClientPassword] = useState('');
  const [newClientPlanId, setNewClientPlanId] = useState('');
  const [newClientDistributorId, setNewClientDistributorId] = useState('');
  const [newClientUtr, setNewClientUtr] = useState('');

  // Manual Subscription Renewal Modal States
  const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);
  const [clientToRenew, setClientToRenew] = useState(null);
  const [renewMonths, setRenewMonths] = useState(1);
  const [renewUtr, setRenewUtr] = useState('');
  const [renewPaymentMethod, setRenewPaymentMethod] = useState('GPay / UPI');
  const [isSubmittingRenew, setIsSubmittingRenew] = useState(false);

  // PLAN ASSIGNMENT ASSISTANT STATE MACHINE
  const [assistantStep, setAssistantStep] = useState(1); // 1: Client select, 2: Plan select, 3: Review & Lockouts, 4: Billing & Confirmation, 5: Complete
  const [assistantClient, setAssistantClient] = useState('');
  const [assistantPlan, setAssistantPlan] = useState('');
  const [isProcessingAssignment, setIsProcessingAssignment] = useState(false);

  // Reset assistant when role changes
  useEffect(() => {
    resetAssistant();
  }, [activeRole]);

  const resetAssistant = () => {
    setAssistantStep(1);
    setAssistantClient('');
    setAssistantPlan('');
    setIsProcessingAssignment(false);
  };

  // -------------------------------------------------------------
  // HANDLERS
  // -------------------------------------------------------------

  // Toggle allowed features for custom plan creation
  const handleToggleFeature = (feature) => {
    if (newPlanFeatures.includes(feature)) {
      setNewPlanFeatures(newPlanFeatures.filter(f => f !== feature));
    } else {
      setNewPlanFeatures([...newPlanFeatures, feature]);
    }
  };

  // Start editing mode for a specific SaaS Plan
  const handleStartEditPlan = (planKey) => {
    const plan = plans[planKey];
    setEditingPlanKey(planKey);
    setNewPlanName(plan.name);
    setNewPlanPrice(plan.price);
    setNewPlanCycle(plan.cycle || 'Monthly');
    setNewPlanDesc(plan.description);
    setNewPlanFeatures(plan.allowedFeatures || []);
    setNewPlanBranchLimit(plan.branchLimit !== undefined ? String(plan.branchLimit) : 'unlimited');
    setNewPlanAdditionalBranchPrice(plan.additionalBranchPrice || 99);
    toast(`Editing plan: ${plan.name}`, { icon: '✏️' });
  };

  // Cancel edit mode and reset form fields
  const handleCancelEditPlan = () => {
    setEditingPlanKey(null);
    setNewPlanName('');
    setNewPlanPrice(49);
    setNewPlanCycle('Monthly');
    setNewPlanDesc('');
    setNewPlanFeatures(['Dashboard', 'Jobs', 'Customers']);
    setNewPlanBranchLimit('unlimited');
    setNewPlanAdditionalBranchPrice(99);
  };

  // Delete a Plan
  const handleDeletePlan = async (planKey) => {
    const plan = plans[planKey];
    if (!plan) return;
    if (!window.confirm(`Delete the "${plan.name}" plan permanently?`)) return;
    try {
      const res = await api.delete(`/api/saas/plans/${plan.id}`);
      if (res.data.success) {
        toast.success(`Plan "${plan.name}" deleted.`);
        if (editingPlanKey === planKey) handleCancelEditPlan();
        await fetchAll();
      } else {
        toast.error(res.data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    }
  };

  // Create or Update a Plan
  const handleCreatePlan = async (e) => {
    e.preventDefault();
    if (!newPlanName.trim()) { toast.error('Please enter a valid plan name.'); return; }

    const branchLimitValue = newPlanBranchLimit === 'unlimited' ? 'unlimited' : parseInt(newPlanBranchLimit) || 2;
    const payload = {
      name: newPlanName,
      price: Number(newPlanPrice),
      cycle: newPlanCycle,
      description: newPlanDesc || `${newPlanName} active tier package.`,
      features: newPlanFeatures.map(f => `${f} Access Module`),
      allowedFeatures: newPlanFeatures,
      branchLimit: String(branchLimitValue),
      additionalBranchPrice: branchLimitValue === 'unlimited' ? 0 : Number(newPlanAdditionalBranchPrice),
    };

    try {
      if (editingPlanKey) {
        const plan = plans[editingPlanKey];
        const res = await api.put(`/api/saas/plans/${plan.id}`, payload);
        if (res.data.success) {
          toast.success(`Plan "${newPlanName}" updated!`);
          handleCancelEditPlan();
          await fetchAll();
        } else { toast.error(res.data.message); }
      } else {
        const res = await api.post('/api/saas/plans', payload);
        if (res.data.success) {
          toast.success(`Plan "${newPlanName}" created!`);
          handleCancelEditPlan();
          await fetchAll();
        } else { toast.error(res.data.message); }
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    }
  };

  // Create a Distributor
  const handleCreateDistributor = async (e) => {
    e.preventDefault();
    if (!newDistName || !newDistEmail || !newDistUsername || !newDistPassword) {
      toast.error('Please provide Name, Email, Username and Password');
      return;
    }
    try {
      const res = await api.post('/api/saas/distributors', {
        name: newDistName, email: newDistEmail,
        username: newDistUsername, password: newDistPassword,
        region: newDistRegion, maxClients: parseInt(newDistMaxClients) || 5,
      });
      if (res.data.success) {
        toast.success(`Distributor "${newDistName}" created!`);
        setNewDistName(''); setNewDistEmail(''); setNewDistRegion('Global');
        setNewDistMaxClients(5); setNewDistUsername(''); setNewDistPassword('');
        await fetchAll();
      } else { toast.error(res.data.message); }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Create distributor failed');
    }
  };

  // Create a Client (Distributor action)
  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!newClientName || !newClientShop || !newClientPhone) {
      toast.error('Please enter Name, Company Name and Phone number');
      return;
    }
    const currentDist = distributors.find(d => d.id === selectedDistributorId);
    const distributorClientCount = clients.filter(c => c.distributorId === selectedDistributorId).length;
    if (currentDist && distributorClientCount >= currentDist.maxClients) {
      toast.error(`Client limit reached (${currentDist.maxClients}) for this distributor.`);
      return;
    }
    try {
      const res = await api.post('/api/saas/clients', {
        name: newClientName, shopName: newClientShop, phone: newClientPhone,
        email: newClientEmail || null, address: newClientAddress,
        city: newClientCity, state: newClientState, pincode: newClientPincode,
        website: newClientWebsite, whatsapp: newClientWhatsapp, gst: newClientGst,
        distributorId: selectedDistributorId,
        username: newClientPhone, // Temp: phone as username; distributor should set properly
        password: 'changeme123', // Temp default password
      });
      if (res.data.success) {
        const newClientId = res.data.data.client.id;
        toast.success(`Client "${newClientName}" created!`);
        setNewClientName(''); setNewClientShop(''); setNewClientPhone('');
        setNewClientEmail(''); setNewClientAddress(''); setNewClientCity('');
        setNewClientState(''); setNewClientPincode(''); setNewClientWebsite('');
        setNewClientWhatsapp(''); setNewClientGst(''); setNewClientDoc(null);
        await fetchAll();
        setAssistantClient(newClientId);
        setAssistantStep(2);
        setAssistantPlan(Object.keys(plans)[0] || 'Starter');
        setDistTab('assistant');
      } else { toast.error(res.data.message); }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Create client failed');
    }
  };

  const handleEditClient = (client) => {
    setEditingClientId(client.id);
    setNewClientName(client.name || '');
    setNewClientShop(client.shopName || '');
    setNewClientPhone(client.phone || '');
    setNewClientEmail(client.email || '');
    setNewClientAddress(client.address || '');
    setNewClientCity(client.city || '');
    setNewClientState(client.state || '');
    setNewClientPincode(client.pincode || '');
    setNewClientWebsite(client.website || '');
    setNewClientWhatsapp(client.whatsapp || '');
    setNewClientGst(client.gst || '');
    setNewClientUsername(client.username || '');
    setNewClientPassword(''); // Don't prefill password
    setNewClientPlanId(client.planId || '');
    setNewClientDistributorId(client.distributorId || '');
    setNewClientUtr(client.utr || '');
    setSaTab('clients'); // Force switch to clients tab so they see form/list
  };

  const handleCancelClientEdit = () => {
    setEditingClientId(null);
    setNewClientName('');
    setNewClientShop('');
    setNewClientPhone('');
    setNewClientEmail('');
    setNewClientAddress('');
    setNewClientCity('');
    setNewClientState('');
    setNewClientPincode('');
    setNewClientWebsite('');
    setNewClientWhatsapp('');
    setNewClientGst('');
    setNewClientUsername('');
    setNewClientPassword('');
    setNewClientPlanId('');
    setNewClientDistributorId('');
    setNewClientUtr('');
  };

  const handleCreateOrUpdateClientByAdmin = async (e) => {
    e.preventDefault();
    if (!newClientName || !newClientShop || !newClientPhone || !newClientUsername) {
      toast.error('Name, Shop Name, Phone, and Username are required');
      return;
    }

    if (!editingClientId && !newClientPassword) {
      toast.error('Password is required for new manual client creation');
      return;
    }

    const payload = {
      name: newClientName,
      shopName: newClientShop,
      phone: newClientPhone,
      email: newClientEmail || null,
      username: newClientUsername,
      password: newClientPassword || undefined,
      planId: newClientPlanId || null,
      distributorId: newClientDistributorId || null,
      utr: newClientUtr || null,
      address: newClientAddress,
      city: newClientCity,
      state: newClientState,
      pincode: newClientPincode,
      website: newClientWebsite,
      whatsapp: newClientWhatsapp,
      gst: newClientGst
    };

    try {
      if (editingClientId) {
        const res = await api.put(`/api/saas/clients/${editingClientId}`, payload);
        if (res.data.success) {
          toast.success(`Client "${newClientName}" updated!`);
          handleCancelClientEdit();
          await fetchAll();
        } else {
          toast.error(res.data.message);
        }
      } else {
        const res = await api.post('/api/saas/clients', payload);
        if (res.data.success) {
          toast.success(`Client "${newClientName}" manually created!`);
          handleCancelClientEdit();
          await fetchAll();
        } else {
          toast.error(res.data.message);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save client');
    }
  };

  const handleToggleClientStatus = async (clientId) => {
    try {
      const res = await api.put(`/api/saas/clients/${clientId}/toggle-status`);
      if (res.data.success) {
        toast.success('Client active status updated!');
        await fetchAll();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to toggle status');
    }
  };

  const handleDeleteClientPermanently = async (clientId, clientName) => {
    if (!window.confirm(`Are you absolutely sure you want to permanently delete client "${clientName}"? This will delete all their payment logs and cannot be undone.`)) {
      return;
    }
    try {
      const res = await api.delete(`/api/saas/clients/${clientId}`);
      if (res.data.success) {
        toast.success(`Client "${clientName}" permanently deleted.`);
        if (editingClientId === clientId) handleCancelClientEdit();
        await fetchAll();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete client');
    }
  };

  // Open Manual Renew Modal (GPay / Offline)
  const handleOpenRenewModal = (client) => {
    setClientToRenew(client);
    setRenewMonths(1);
    setRenewUtr(client.utr || '');
    setRenewPaymentMethod('GPay / UPI');
    setIsRenewModalOpen(true);
  };

  // Execute Manual Renewal via API
  const handleConfirmRenew = async () => {
    if (!clientToRenew) return;
    setIsSubmittingRenew(true);
    try {
      const res = await api.post(`/api/saas/clients/${clientToRenew.id}/renew`, {
        months: Number(renewMonths),
        utr: renewUtr ? renewUtr.trim() : undefined,
        paymentMethod: renewPaymentMethod,
      });
      if (res.data.success) {
        toast.success(res.data.message || 'Subscription renewed successfully!');
        setIsRenewModalOpen(false);
        setClientToRenew(null);
        await fetchAll();
      } else {
        toast.error(res.data.message || 'Renewal failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to renew subscription');
    } finally {
      setIsSubmittingRenew(false);
    }
  };

  // Execute Plan Assignment via API
  const handleExecuteAssignment = async () => {
    setIsProcessingAssignment(true);
    try {
      const clientToUpdate = clients.find(c => c.id === assistantClient);
      const planObj = plans[assistantPlan];

      if (!clientToUpdate || !planObj) {
        toast.error('Assignment failed: Invalid details.');
        setIsProcessingAssignment(false);
        return;
      }

      const res = await api.put(`/api/saas/clients/${assistantClient}/assign-plan`, {
        planId: planObj.id
      });

      if (res.data.success) {
        await fetchAll();
        setIsProcessingAssignment(false);
        setAssistantStep(5);
        toast.success('Subscription Plan assigned!');
      } else {
        toast.error(res.data.message || 'Assignment failed');
        setIsProcessingAssignment(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Assignment failed');
      setIsProcessingAssignment(false);
    }
  };

  // Helper selectors
  const activeDistributor = distributors.find(d => d.id === selectedDistributorId);
  const activeClient = clients.find(c => c.id === selectedClientId);
  const distributorClients = clients.filter(c => c.distributorId === selectedDistributorId);

  // MRR Calculation
  const calculateMRR = () => {
    return clients.reduce((total, c) => {
      if (c.plan) {
        if (plans[c.plan]) return total + Number(plans[c.plan].price);
        if (c.plan === 'Enterprise') return total + 249;
        if (c.plan === 'Growth') return total + 99;
        if (c.plan === 'Starter') return total + 49;
      }
      return total;
    }, 0);
  };

  const getPlanPriceINR = (planName) => {
    const customPlan = plans[planName];
    if (customPlan) return Number(customPlan.price);
    if (planName === 'Enterprise') return 249;
    if (planName === 'Growth') return 99;
    if (planName === 'Starter') return 49;
    return 49;
  };

  

  return (
    <div className="p-6 bg-[#F8FAFC] min-h-screen text-slate-700 font-sans">
      {/* SaaS Hub Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between border-b border-blue-200 pb-6 mb-8 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="bg-blue-100 text-blue-700 text-xs px-2.5 py-1 rounded-full font-semibold uppercase tracking-wider flex items-center gap-1 border border-blue-200">
              <Sparkles className="w-3.5 h-3.5" /> Multi-Tenant System
            </span>
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight flex items-center gap-3">
            <Building2 className="w-8 h-8 text-blue-600" /> SaaS Subscription Management Portal
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            Simulate and manage the multi-tier hierarchy: Super Admin, Distributors, and Client Store Admins.
          </p>
        </div>

        {/* Header Profile Panel & Log Out */}
        {user && (
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-bold text-slate-900 leading-none">{user.firstName} {user.lastName}</p>
              <p className="text-[10px] text-blue-600 font-semibold mt-1">{user.email}</p>
            </div>
            <button
              onClick={logout}
              className="bg-white hover:bg-red-50 hover:text-red-600 border border-blue-200 hover:border-red-200 text-slate-700 font-bold px-4 py-2 rounded-xl text-xs transition-all flex items-center gap-1.5 shadow-sm active:scale-95"
            >
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        )}

        {/* Role Picker (hidden for specific SaaS multi-tier users, visible only for developer mock testing) */}
        {(!user || (!['SUPER_ADMIN', 'DISTRIBUTOR', 'CLIENT'].includes(user.role))) && (
          <div className="bg-white p-1.5 rounded-xl border border-blue-200 flex gap-1 shadow-sm">
            <button 
              onClick={() => setActiveRole('superadmin')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2 ${
                activeRole === 'superadmin' 
                  ? 'bg-blue-600 text-white shadow-sm shadow-blue-900/30' 
                  : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
              }`}
            >
              <ShieldAlert className="w-4 h-4" /> Super Admin
            </button>
            
            <button 
              onClick={() => setActiveRole('distributor')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2 ${
                activeRole === 'distributor' 
                  ? 'bg-blue-600 text-white shadow-sm shadow-blue-900/30' 
                  : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
              }`}
            >
              <Users className="w-4 h-4" /> Distributor
            </button>

            <button 
              onClick={() => setActiveRole('client')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2 ${
                activeRole === 'client' 
                  ? 'bg-blue-600 text-white shadow-sm shadow-blue-900/30' 
                  : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
              }`}
            >
              <Layers className="w-4 h-4" /> Client Portal
            </button>
          </div>
        )}

      </div>

      {/* Global Alerts & Switchers */}
      {activeRole === 'distributor' && (
        <div className="space-y-6 animate-fadeIn">
          {/* Sub-tab Navigation Header */}
          <div className="flex border-b border-blue-200 mb-6 bg-white p-2 rounded-xl shadow-sm gap-2">
            <button 
              onClick={() => setDistTab('overview')}
              className={`px-4 py-2 text-xs font-extrabold flex items-center gap-1.5 rounded-lg transition-all ${
                distTab === 'overview' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <LayoutDashboard className="w-4 h-4" /> Overview Dashboard
            </button>
            <button 
              onClick={() => setDistTab('provision')}
              className={`px-4 py-2 text-xs font-extrabold flex items-center gap-1.5 rounded-lg transition-all ${
                distTab === 'provision' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <PlusCircle className="w-4 h-4" /> Provision Shop Admin
            </button>
            <button 
              onClick={() => setDistTab('clients')}
              className={`px-4 py-2 text-xs font-extrabold flex items-center gap-1.5 rounded-lg transition-all ${
                distTab === 'clients' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Building2 className="w-4 h-4" /> Manage Client Shops
            </button>
            <button 
              onClick={() => setDistTab('assistant')}
              className={`px-4 py-2 text-xs font-extrabold flex items-center gap-1.5 rounded-lg transition-all ${
                distTab === 'assistant' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Bot className="w-4 h-4" /> SaaS Plan Assistant
            </button>
          </div>

          {/* TAB 1: OVERVIEW DASHBOARD */}
          {distTab === 'overview' && (
            <div className="space-y-6 animate-fadeIn">
              {/* Scope Metrics Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-sm relative overflow-hidden group">
                  <div className="absolute right-0 top-0 w-24 h-24 bg-blue-500/5 rounded-full blur-xl" />
                  <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Assigned Channel Scope</p>
                  <p className="text-2xl font-extrabold text-slate-900 mt-2">{activeDistributor?.name}</p>
                  <div className="mt-4 flex items-center text-xs text-slate-500 gap-1">
                    <Building2 className="w-3.5 h-3.5 text-blue-600" /> Sector: {activeDistributor?.region}
                  </div>
                </div>

                <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-sm relative overflow-hidden group">
                  <div className="absolute right-0 top-0 w-24 h-24 bg-blue-500/5 rounded-full blur-xl" />
                  <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">License Occupancy</p>
                  <p className="text-3xl font-extrabold text-slate-900 mt-2 flex items-baseline gap-1">
                    {distributorClients.length} <span className="text-slate-400 text-sm font-normal">/ {activeDistributor?.maxClients} Max Shops</span>
                  </p>
                  <div className="mt-4 flex items-center text-xs text-blue-600 gap-1 font-semibold">
                    <TrendingUp className="w-3.5 h-3.5" /> {activeDistributor?.maxClients - distributorClients.length} unused license quotas
                  </div>
                </div>

                <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-sm relative overflow-hidden group">
                  <div className="absolute right-0 top-0 w-24 h-24 bg-blue-500/5 rounded-full blur-xl" />
                  <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Direct Contact Account</p>
                  <p className="text-lg font-bold text-slate-900 mt-2 font-mono truncate">{activeDistributor?.email}</p>
                  <div className="mt-4 flex items-center text-xs text-slate-500 gap-1">
                    <Users className="w-3.5 h-3.5 text-blue-600" /> Multi-Tenant Channel Operator
                  </div>
                </div>
              </div>

              {/* Graphical Segment Dashboard */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* SVG Progress Ring for Quota */}
                <div className="bg-white border border-blue-100 rounded-2xl p-6 shadow-sm flex flex-col justify-between items-center text-center">
                  <div className="w-full flex justify-between items-center mb-4 border-b border-gray-50 pb-2">
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                      <Sparkles className="w-4 h-4 text-indigo-500" /> Channel License Utilization
                    </h3>
                    <span className="text-[10px] bg-indigo-50 text-indigo-650 px-2 py-0.5 rounded font-mono font-bold">
                      Real-time Quota
                    </span>
                  </div>
                  
                  {/* Circular Ring SVG Chart */}
                  <div className="relative w-40 h-40 flex items-center justify-center my-2">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="40" stroke="#f1f5f9" strokeWidth="8" fill="transparent" />
                      <circle cx="50" cy="50" r="40" stroke="url(#blueGradient)" strokeWidth="8" fill="transparent"
                        strokeDasharray={2 * Math.PI * 40}
                        strokeDashoffset={2 * Math.PI * 40 * (1 - (distributorClients.length / (activeDistributor?.maxClients || 5)))}
                        strokeLinecap="round"
                        className="transition-all duration-1000 ease-out"
                      />
                      <defs>
                        <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#2563eb" />
                          <stop offset="100%" stopColor="#4f46e5" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute flex flex-col items-center">
                      <span className="text-3xl font-extrabold text-slate-800">
                        {Math.round((distributorClients.length / (activeDistributor?.maxClients || 5)) * 100)}%
                      </span>
                      <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold mt-1">Occupied</span>
                    </div>
                  </div>

                  <p className="text-xs text-slate-500 mt-2 font-medium">
                    You have provisioned <strong className="text-slate-800">{distributorClients.length}</strong> out of <strong className="text-slate-800">{activeDistributor?.maxClients || 5}</strong> total available client accounts.
                  </p>
                </div>

                {/* SVG Bar Chart for Subscription Tiers */}
                <div className="bg-white border border-blue-100 rounded-2xl p-6 shadow-sm flex flex-col justify-between">
                  <div className="w-full flex justify-between items-center mb-4 border-b border-gray-50 pb-2">
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                      <TrendingUp className="w-4 h-4 text-emerald-500" /> Plan Distribution Segments
                    </h3>
                    <span className="text-[10px] bg-emerald-50 text-emerald-650 px-2 py-0.5 rounded font-mono font-bold">
                      Subscribers
                    </span>
                  </div>

                  <div className="space-y-4 my-2 flex-1 flex flex-col justify-center">
                    {[
                      { plan: 'Enterprise', count: distributorClients.filter(c => c.plan === 'Enterprise').length, color: 'bg-emerald-500' },
                      { plan: 'Growth', count: distributorClients.filter(c => c.plan === 'Growth').length, color: 'bg-blue-500' },
                      { plan: 'Starter', count: distributorClients.filter(c => c.plan === 'Starter').length, color: 'bg-indigo-500' },
                      { plan: 'Unassigned', count: distributorClients.filter(c => c.plan === 'Unassigned').length, color: 'bg-slate-300' }
                    ].map((seg, idx) => {
                      const pct = distributorClients.length > 0 ? (seg.count / distributorClients.length) * 100 : 0;
                      return (
                        <div key={idx} className="space-y-1">
                          <div className="flex justify-between text-xs font-semibold">
                            <span className="text-slate-700">{seg.plan} Plan</span>
                            <span className="text-slate-500 font-mono">{seg.count} ({Math.round(pct)}%)</span>
                          </div>
                          <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                            <div className={`h-full ${seg.color} rounded-full transition-all duration-1000`} style={{ width: `${pct}%` }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-[11px] text-slate-400 font-medium text-center border-t border-gray-50 pt-2.5 mt-2">
                    Visual segments of plan classifications active in your distribution region.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* TAB 1.5: MANAGE CLIENT SHOPS */}
          {distTab === 'clients' && (
            <div className="bg-white border border-blue-100 rounded-2xl p-6 shadow-sm animate-fadeIn">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-600" /> Active Clients under your Scope
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Managing clients for <span className="text-blue-600 font-bold">{activeDistributor?.name}</span>
                  </p>
                </div>
                <span className="bg-blue-100 text-blue-700 text-xs px-2.5 py-1 rounded-full font-semibold border border-blue-200">
                  Total: {distributorClients.length} clients
                </span>
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded-xl bg-white">
                <table className="min-w-full divide-y divide-gray-150">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Client Business</th>
                      <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Store Admin</th>
                      <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Active SaaS Plan</th>
                      <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Next Billing</th>
                      <th className="px-5 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm">
                    {distributorClients.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-5 py-8 text-center text-slate-400 italic">No client shops registered under your distributor channel yet.</td>
                      </tr>
                    ) : (
                      distributorClients.map(c => (
                        <tr key={c.id} className="hover:bg-blue-50/10 transition-colors">
                          <td className="px-5 py-3">
                            <div className="font-bold text-slate-900 text-sm">{c.shopName}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5 font-mono">{c.phone}</div>
                          </td>
                          <td className="px-5 py-3">
                            <div className="text-slate-800 font-medium">{c.name}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5">{c.email}</div>
                          </td>
                          <td className="px-5 py-3">
                            <span className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-full border ${
                              c.plan === 'Enterprise' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              c.plan === 'Growth' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                              c.plan === 'Starter' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                              'bg-gray-100 text-gray-500 border border-gray-200'
                            }`}>
                              {c.plan}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-slate-600 font-mono text-xs">
                            {c.nextBilling ? c.nextBilling : '--'}
                          </td>
                          <td className="px-5 py-3 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                onClick={() => handleOpenRenewModal(c)}
                                className="bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white border border-emerald-500/20 text-xs font-bold px-3 py-1.5 rounded-lg transition-all shadow-sm flex items-center gap-1"
                                title="Manual Renewal (GPay / Cash / UPI)"
                              >
                                <RefreshCw className="w-3 h-3" />
                                <span>Renew (GPay)</span>
                              </button>
                              <button
                                onClick={() => {
                                  setAssistantClient(c.id);
                                  setAssistantStep(2);
                                  setAssistantPlan(c.plan !== 'Unassigned' ? c.plan : 'Starter');
                                  setDistTab('assistant');
                                }}
                                className="bg-blue-50 hover:bg-blue-600 text-blue-700 hover:text-white border border-blue-200 text-xs font-bold px-2.5 py-1.5 rounded-lg transition-all shadow-sm"
                              >
                                Change Plan
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 2: PROVISION SHOP ADMIN */}
          {distTab === 'provision' && (
            <div className="max-w-4xl bg-white border border-blue-100 rounded-2xl p-6 shadow-sm animate-fadeIn">
              <h2 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
                <PlusCircle className="w-5 h-5 text-blue-600" /> Create Client Shop Account
              </h2>
              <p className="text-xs text-slate-500 mb-6">
                Register a new retail or automotive shop client under your distributor tenancy.
              </p>

              <form onSubmit={(e) => {
                handleCreateClient(e);
              }} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Owner / Admin Name</label>
                  <input
                    type="text"
                    required
                    value={newClientName}
                    onChange={(e) => setNewClientName(e.target.value)}
                    placeholder="e.g., Jeevaraajan"
                    className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Company Name *</label>
                  <input
                    type="text"
                    required
                    value={newClientShop}
                    onChange={(e) => setNewClientShop(e.target.value)}
                    placeholder="e.g., Repair Shop"
                    className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Phone</label>
                  <input
                    type="text"
                    required
                    value={newClientPhone}
                    onChange={(e) => setNewClientPhone(e.target.value)}
                    placeholder="e.g., 08056339539"
                    className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={newClientEmail}
                    onChange={(e) => setNewClientEmail(e.target.value)}
                    placeholder="e.g., jeevaraajan627@gmail.com"
                    className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Address</label>
                  <input
                    type="text"
                    value={newClientAddress}
                    onChange={(e) => setNewClientAddress(e.target.value)}
                    placeholder="e.g., Gangai amman koil street, pangur, thennal post"
                    className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">City</label>
                  <input
                    type="text"
                    value={newClientCity}
                    onChange={(e) => setNewClientCity(e.target.value)}
                    placeholder="e.g., PUDUCHERRY"
                    className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">State</label>
                  <input
                    type="text"
                    value={newClientState}
                    onChange={(e) => setNewClientState(e.target.value)}
                    placeholder="e.g., puducherry"
                    className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Pincode</label>
                  <input
                    type="text"
                    value={newClientPincode}
                    onChange={(e) => setNewClientPincode(e.target.value)}
                    placeholder="e.g., 605102"
                    className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Website</label>
                  <input
                    type="url"
                    value={newClientWebsite}
                    onChange={(e) => setNewClientWebsite(e.target.value)}
                    placeholder="e.g., https://www.smartgonext.com/"
                    className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">STD Code (for WhatsApp)</label>
                  <input
                    type="text"
                    value={newClientWhatsapp}
                    onChange={(e) => setNewClientWhatsapp(e.target.value)}
                    placeholder="e.g., +91 9655321915"
                    className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">GST Number</label>
                  <input
                    type="text"
                    value={newClientGst}
                    onChange={(e) => setNewClientGst(e.target.value)}
                    placeholder="e.g., 36AAAAA1111A1Z1"
                    className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2 bg-blue-50/30 border border-blue-100 rounded-xl p-4">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Verification Document Upload (Optional)</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      onChange={(e) => setNewClientDoc(e.target.files ? e.target.files[0] : null)}
                      className="text-xs text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {newClientDoc && (
                      <span className="text-xs text-emerald-600 font-semibold font-mono">
                        Selected: {newClientDoc.name}
                      </span>
                    )}
                  </div>
                </div>

                <div className="md:col-span-2 flex justify-end mt-2">
                  <button
                    type="submit"
                    className="w-full md:w-auto bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-bold px-6 py-3 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 border border-blue-400/20 shadow-sm"
                  >
                    <Plus className="w-4 h-4" /> Provision Client Portal
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* TAB 3: SAAS PLAN ASSIGNMENT ASSISTANT */}
          {distTab === 'assistant' && (
            <div className="max-w-3xl bg-white border-2 border-blue-200 rounded-3xl p-6 shadow-md relative overflow-hidden flex flex-col justify-between min-h-[500px] animate-fadeIn">
              {/* Decorative Glow */}
              <div className="absolute right-0 top-0 w-32 h-32 bg-blue-500/5 rounded-full blur-2xl pointer-events-none" />
              <div className="absolute left-0 bottom-0 w-32 h-32 bg-indigo-500/5 rounded-full blur-2xl pointer-events-none" />

              <div>
                {/* Assistant Title Header */}
                <div className="flex items-center gap-3 border-b border-blue-100 pb-4 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md animate-pulse">
                    <Bot className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-slate-800 text-base flex items-center gap-1.5">
                      SaaS Plan Assistant <Sparkles className="w-4 h-4 text-blue-500" />
                    </h3>
                    <p className="text-[11px] text-blue-600 font-semibold">Assisting Distributor Plan Workflows</p>
                  </div>
                </div>

                {/* STEP 1: SELECT CLIENT */}
                {assistantStep === 1 && (
                  <div className="space-y-4 animate-fadeIn">
                    <div className="bg-blue-50/50 p-4 rounded-2xl border border-blue-100">
                      <p className="text-sm text-slate-700 leading-relaxed mb-3">
                        "Welcome! Let's activate a subscription license. First, select the client business you want to assign a billing plan to."
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Select Client Shop</label>
                      <select
                        value={assistantClient}
                        onChange={(e) => setAssistantClient(e.target.value)}
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">-- Choose registered shop --</option>
                        {distributorClients.map(c => (
                          <option key={c.id} value={c.id}>{c.shopName} ({c.name})</option>
                        ))}
                      </select>
                    </div>

                    <button
                      disabled={!assistantClient}
                      onClick={() => setAssistantStep(2)}
                      className={`w-full mt-4 font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 border shadow-sm ${
                        assistantClient 
                          ? 'bg-blue-600 hover:bg-blue-500 text-white border-blue-400/20' 
                          : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      }`}
                    >
                      Continue to Plan Selection <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* STEP 2: SELECT PLAN */}
                {assistantStep === 2 && (
                  <div className="space-y-4 animate-fadeIn">
                    <div className="bg-blue-50 p-3.5 rounded-xl border border-blue-100 text-xs mb-2">
                      <span className="text-slate-500">Client:</span> <strong className="text-slate-800">{clients.find(c => c.id === assistantClient)?.shopName}</strong>
                    </div>

                    <p className="text-sm text-slate-700">
                      "Which plan is most suitable for their business constraints? Choose a tier to customize features."
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {Object.keys(plans).map(planKey => {
                        const plan = plans[planKey];
                        return (
                          <div
                            key={planKey}
                            onClick={() => setAssistantPlan(planKey)}
                            className={`cursor-pointer border-2 rounded-2xl p-4 transition-all hover:scale-[1.02] relative overflow-hidden flex flex-col justify-between ${
                              assistantPlan === planKey 
                                ? 'border-blue-600 bg-blue-50/20 shadow-sm' 
                                : 'border-gray-200 bg-white hover:border-blue-300'
                            }`}
                          >
                            {assistantPlan === planKey && (
                              <div className="absolute top-0 right-0 bg-blue-600 text-white text-[9px] px-2 py-0.5 rounded-bl font-extrabold uppercase tracking-wide">
                                Selected
                              </div>
                            )}
                            <div>
                              <h4 className="font-extrabold text-slate-850 text-sm">{plan.name}</h4>
                              <p className="text-slate-400 text-[10px] mt-1 font-mono uppercase tracking-wider">{plan.cycle}</p>
                              <div className="text-slate-900 font-extrabold text-xl mt-2 font-mono flex items-baseline">
                                ₹{plan.price} <span className="text-[10px] text-slate-400 font-normal">/mo</span>
                              </div>
                            </div>
                            <div className="border-t border-gray-100 mt-3 pt-2">
                              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-1">Max Devices limit:</p>
                              <p className="text-xs font-semibold text-slate-700">{plan.maxDevices}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <button
                        onClick={() => setAssistantStep(1)}
                        className="bg-gray-150 hover:bg-gray-250 text-slate-700 font-bold py-2.5 rounded-xl text-sm transition-all border border-gray-200"
                      >
                        Back
                      </button>
                      <button
                        onClick={() => setAssistantStep(3)}
                        className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2.5 rounded-xl text-sm border border-blue-400/20 transition-all flex items-center justify-center gap-1 shadow-sm"
                      >
                        Active Features <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* STEP 3: SHOW ACTIVE FEATURES FOR CLIENT */}
                {assistantStep === 3 && (
                  <div className="space-y-4 animate-fadeIn">
                    <div className="bg-blue-50 p-3.5 rounded-xl border border-blue-100 text-xs flex justify-between items-center">
                      <div>
                        <span className="text-slate-500">Client:</span> <strong className="text-slate-800">{clients.find(c => c.id === assistantClient)?.shopName}</strong>
                      </div>
                      <span className="bg-white px-2 py-0.5 rounded border border-blue-200 font-bold text-blue-600 font-mono text-[10px]">
                        Plan: {assistantPlan}
                      </span>
                    </div>

                    <p className="text-sm text-slate-700">
                      "Here are the active features that will be fully unlocked for this shop based on the selected tier structure:"
                    </p>

                    <div className="bg-slate-50 border border-gray-150 rounded-2xl p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 max-h-[180px] overflow-y-auto pr-1">
                        {(plans[assistantPlan] || plans.Starter).features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs text-slate-700 bg-white p-2.5 rounded-lg border border-gray-100 hover:border-blue-200 transition-colors shadow-sm">
                            <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                            <span className="truncate font-semibold">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <button
                        onClick={() => setAssistantStep(2)}
                        className="bg-gray-100 hover:bg-gray-200 text-slate-700 font-bold py-2.5 rounded-xl text-sm transition-all"
                      >
                        Change Plan
                      </button>
                      <button
                        onClick={() => setAssistantStep(4)}
                        className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2.5 rounded-xl text-sm border border-blue-400/20 transition-all flex items-center justify-center gap-1 shadow-sm"
                      >
                        Billing Review <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* STEP 4: BILLING CYCLE & FINAL CONFIRMATION */}
                {assistantStep === 4 && (
                  <div className="space-y-4 animate-fadeIn">
                    <p className="text-sm text-slate-700">
                      "Please review the invoice scheduling and final details before authorizing the license activation."
                    </p>

                    {/* Summary Statement */}
                    <div className="bg-slate-50 border border-gray-200 rounded-2xl p-4 space-y-3 font-mono text-xs text-slate-700">
                      <div className="flex justify-between border-b border-gray-100 pb-2">
                        <span className="text-slate-400 font-sans font-bold">CLIENT ADMIN:</span>
                        <span className="text-slate-900 font-bold">{clients.find(c => c.id === assistantClient)?.name}</span>
                      </div>
                      <div className="flex justify-between border-b border-gray-100 pb-2">
                        <span className="text-slate-400 font-sans font-bold">RETAIL SHOP:</span>
                        <span className="text-slate-900 font-bold">{clients.find(c => c.id === assistantClient)?.shopName}</span>
                      </div>
                      <div className="flex justify-between border-b border-gray-100 pb-2">
                        <span className="text-slate-400 font-sans font-bold">BILLING TIER:</span>
                        <span className="text-blue-600 font-bold">{assistantPlan} Subscription</span>
                      </div>
                      <div className="flex justify-between border-b border-gray-100 pb-2">
                        <span className="text-slate-400 font-sans font-bold">BILLING CYCLE:</span>
                        <span className="text-slate-800 font-bold">{(plans[assistantPlan] || plans.Starter).cycle}</span>
                      </div>
                      <div className="flex justify-between border-b border-gray-100 pb-2">
                        <span className="text-slate-400 font-sans font-bold">NEXT DUE DATE:</span>
                        <span className="text-slate-800 font-bold font-mono">
                          {(() => {
                            const d = new Date();
                            d.setDate(d.getDate() + 30);
                            return d.toISOString().split('T')[0];
                          })()}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm pt-1">
                        <span className="text-emerald-600 font-bold font-sans">BILLING CHARGE:</span>
                        <span className="text-emerald-600 font-extrabold font-mono">₹{(plans[assistantPlan] || plans.Starter).price} INR</span>
                      </div>
                    </div>

                    {/* Payment Method Selector */}
                    <div className="mt-3 mb-3">
                      <span className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Select Payment Method *</span>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setPaymentMethod('razorpay')}
                          className={`py-2 px-3 rounded-xl border transition-all flex items-center justify-center gap-1.5 font-bold text-xs ${
                            paymentMethod === 'razorpay'
                              ? 'bg-blue-50/50 border-blue-400 text-blue-700 shadow-sm'
                              : 'bg-white border-gray-200 text-slate-650 hover:border-blue-200'
                          }`}
                        >
                          💳 Razorpay
                        </button>
                        <button
                          type="button"
                          onClick={() => setPaymentMethod('paypal')}
                          className={`py-2 px-3 rounded-xl border transition-all flex items-center justify-center gap-1.5 font-bold text-xs ${
                            paymentMethod === 'paypal'
                              ? 'bg-blue-50/50 border-blue-400 text-blue-700 shadow-sm'
                              : 'bg-white border-gray-200 text-slate-650 hover:border-blue-200'
                          }`}
                        >
                          🅿️ PayPal
                        </button>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex gap-2.5 items-start">
                      <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                      <p className="text-[10px] text-blue-800 leading-normal">
                        <strong>Super Admin Notification:</strong> Confirming this subscription broadcasts this contract directly to the Super Admin's financial ledger.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <button
                        onClick={() => setAssistantStep(3)}
                        className="bg-gray-100 hover:bg-gray-200 text-slate-700 font-bold py-2.5 rounded-xl text-sm transition-all"
                      >
                        Back
                      </button>
                      <button
                        onClick={() => {
                          if (paymentMethod === 'paypal') {
                            setIsPayPalOpen(true);
                          } else {
                            setIsRazorpayOpen(true);
                          }
                        }}
                        disabled={isProcessingAssignment}
                        className="bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white font-extrabold py-2.5 rounded-xl text-sm border border-emerald-400/20 transition-all flex items-center justify-center gap-2 shadow-sm"
                      >
                        {isProcessingAssignment ? (
                          <>
                            <div className="spinner w-4 h-4" /> Activating...
                          </>
                        ) : (
                          <>
                            Confirm & Assign Plan
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* STEP 5: ASSIGNMENT SUMMARY */}
                {assistantStep === 5 && (
                  <div className="space-y-4 animate-fadeIn">
                    {/* Success Icon */}
                    <div className="flex flex-col items-center justify-center text-center py-2">
                      <div className="w-12 h-12 bg-emerald-50 border border-emerald-200 rounded-full flex items-center justify-center text-emerald-600 mb-2">
                        <Check className="w-7 h-7" />
                      </div>
                      <h4 className="font-extrabold text-slate-800 text-lg">Plan Assigned Successfully!</h4>
                      <p className="text-xs text-emerald-600 mt-1 font-semibold">Super Admin dashboard has been updated.</p>
                    </div>

                    <div className="bg-slate-50 border border-gray-150 rounded-2xl p-4 space-y-2.5 text-xs text-slate-700">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Client Shop:</span>
                        <span className="text-slate-800 font-bold">{clients.find(c => c.id === assistantClient)?.shopName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Assigned Plan:</span>
                        <span className="text-blue-600 font-bold font-mono">{assistantPlan}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Billing Cycle:</span>
                        <span className="text-slate-700">{(plans[assistantPlan] || plans.Starter).cycle}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Next Billing Date:</span>
                        <span className="text-slate-800 font-mono font-bold">{clients.find(c => c.id === assistantClient)?.nextBilling}</span>
                      </div>
                    </div>

                    {/* Active features scroll preview */}
                    <div className="bg-slate-100 p-3 rounded-xl border border-gray-200">
                      <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-2">Activated Shop Features:</p>
                      <div className="max-h-[80px] overflow-y-auto space-y-1.5">
                        {(plans[assistantPlan] || plans.Starter).features.map((feat, i) => (
                          <div key={i} className="flex items-center gap-2 text-[10px] text-slate-600">
                            <Check className="w-3 h-3 text-emerald-600 shrink-0" />
                            <span className="truncate">{feat}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={resetAssistant}
                      className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-xl text-sm transition-all shadow-sm"
                    >
                      Assign Another Client Plan
                    </button>
                  </div>
                )}

              </div>

              {/* Progress Tracker dots */}
              {assistantStep < 5 && (
                <div className="flex items-center justify-center gap-2 border-t border-gray-100 pt-4 mt-6">
                  {[1, 2, 3, 4].map(step => (
                    <div
                      key={step}
                      onClick={() => {
                        if (step < assistantStep) setAssistantStep(step);
                      }}
                      className={`h-2 rounded-full cursor-pointer transition-all duration-300 ${
                        step === assistantStep 
                          ? 'w-6 bg-blue-500' 
                          : step < assistantStep 
                            ? 'w-2 bg-emerald-500' 
                            : 'w-2 bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              )}

            </div>
          )}
        </div>
      )}

      {activeRole === 'client' && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center border border-blue-200 text-blue-600 font-bold">
              C
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Impersonating Client Shop Admin</p>
              <h3 className="font-bold text-slate-900 text-base">
                {activeClient?.shopName || 'No Shop'} 
                <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200">
                  Plan: {activeClient?.plan || 'Unassigned'}
                </span>
              </h3>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 font-medium">Select Client:</span>
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              className="bg-white border border-blue-200 text-slate-800 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
            >
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.shopName} ({c.name})</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* -------------------------------------------------------------------------------------------------------------------------------- */}
      {/* 1. SUPER ADMIN WORKSPACE */}
      {/* -------------------------------------------------------------------------------------------------------------------------------- */}
      {activeRole === 'superadmin' && (
        <div className="space-y-6 animate-fadeIn">
          {/* Sub-tab Navigation Header */}
          <div className="flex border-b border-blue-200 mb-6 bg-white p-2 rounded-xl shadow-sm gap-2">
            <button 
              onClick={() => setSaTab('overview')}
              className={`px-4 py-2 text-xs font-extrabold flex items-center gap-1.5 rounded-lg transition-all ${
                saTab === 'overview' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <LayoutDashboard className="w-4 h-4" /> Overview Dashboard
            </button>
            <button 
              onClick={() => setSaTab('distributors')}
              className={`px-4 py-2 text-xs font-extrabold flex items-center gap-1.5 rounded-lg transition-all ${
                saTab === 'distributors' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Users className="w-4 h-4" /> Manage Distributors
            </button>
            <button 
              onClick={() => setSaTab('reports')}
              className={`px-4 py-2 text-xs font-extrabold flex items-center gap-1.5 rounded-lg transition-all ${
                saTab === 'reports' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <BarChart3 className="w-4 h-4" /> Financial Reports
            </button>
            <button 
              onClick={() => setSaTab('plans')}
              className={`px-4 py-2 text-xs font-extrabold flex items-center gap-1.5 rounded-lg transition-all ${
                saTab === 'plans' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Coins className="w-4 h-4" /> SaaS Subscription Plans
            </button>
            <button 
              onClick={() => setSaTab('clients')}
              className={`px-4 py-2 text-xs font-extrabold flex items-center gap-1.5 rounded-lg transition-all ${
                saTab === 'clients' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Building2 className="w-4 h-4" /> Manage Customers
            </button>
          </div>

          {/* TAB 1: OVERVIEW DASHBOARD */}
          {saTab === 'overview' && (
            <div className="space-y-6 animate-fadeIn">
              {/* Stats Bar */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-sm relative overflow-hidden group">
                  <div className="absolute right-0 top-0 w-24 h-24 bg-blue-500/5 rounded-full blur-xl group-hover:scale-125 transition-transform duration-500" />
                  <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Total Distributors</p>
                  <p className="text-3xl font-extrabold text-slate-900 mt-2 flex items-baseline gap-2">
                    {distributors.length} <span className="text-xs text-blue-600 font-normal">Active partners</span>
                  </p>
                  <div className="mt-4 flex items-center text-xs text-slate-500 gap-1">
                    <Building2 className="w-3.5 h-3.5 text-blue-600" /> Across {new Set(distributors.map(d => d.region)).size} regional sectors
                  </div>
                </div>

                <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-sm relative overflow-hidden group">
                  <div className="absolute right-0 top-0 w-24 h-24 bg-blue-500/5 rounded-full blur-xl group-hover:scale-125 transition-transform duration-500" />
                  <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Active Client Stores</p>
                  <p className="text-3xl font-extrabold text-slate-900 mt-2 flex items-baseline gap-2">
                    {clients.length} <span className="text-xs text-blue-600 font-normal">Registered tenants</span>
                  </p>
                  <div className="mt-4 flex items-center text-xs text-emerald-600 gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5 animate-pulse" /> {clients.filter(c => c.plan !== 'Unassigned').length} stores subscribed
                  </div>
                </div>

                <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-sm relative overflow-hidden group">
                  <div className="absolute right-0 top-0 w-24 h-24 bg-blue-500/5 rounded-full blur-xl group-hover:scale-125 transition-transform duration-500" />
                  <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Monthly Recurring Revenue</p>
                  <p className="text-3xl font-extrabold text-slate-900 mt-2 flex items-baseline gap-2">
                    ₹{calculateMRR()} <span className="text-xs text-blue-600 font-normal">INR / mo</span>
                  </p>
                  <div className="mt-4 flex items-center text-xs text-slate-500 gap-1">
                    <TrendingUp className="w-3.5 h-3.5 text-blue-500" /> Real-time active license sum
                  </div>
                </div>

                <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-sm relative overflow-hidden group">
                  <div className="absolute right-0 top-0 w-24 h-24 bg-blue-500/5 rounded-full blur-xl group-hover:scale-125 transition-transform duration-500" />
                  <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Enterprise Tiers</p>
                  <p className="text-3xl font-extrabold text-slate-900 mt-2 flex items-baseline gap-2">
                    {clients.filter(c => c.plan === 'Enterprise').length} <span className="text-xs text-blue-600 font-normal">Premium accounts</span>
                  </p>
                  <div className="mt-4 flex items-center text-xs text-blue-600 gap-1">
                    <Sparkles className="w-3.5 h-3.5 text-blue-500" /> High tier service enabled
                  </div>
                </div>
              </div>

              {/* Super Admin Dashboard Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* 1. SVG Revenue Growth Wave Line Chart (takes 2/3 cols) */}
                <div className="lg:col-span-2 bg-white border border-blue-100 rounded-2xl p-6 shadow-sm flex flex-col justify-between">
                  <div className="w-full flex justify-between items-center mb-6 border-b border-gray-50 pb-3">
                    <div>
                      <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                        <TrendingUp className="w-4 h-4 text-blue-600" /> SaaS Revenue Growth Trend
                      </h3>
                      <p className="text-[10px] text-slate-400 font-medium">Monthly recurring revenue (MRR) trajectory in Rupees (₹)</p>
                    </div>
                    <span className="text-[10px] bg-blue-50 text-blue-750 px-2 py-0.5 rounded font-mono font-bold">
                      Active Cycle
                    </span>
                  </div>

                  {/* SVG Wave Line Chart */}
                  <div className="w-full h-56 mt-2 relative">
                    <svg className="w-full h-full" viewBox="0 0 500 200" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.25" />
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                        </linearGradient>
                        <linearGradient id="strokeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#6366f1" />
                          <stop offset="50%" stopColor="#3b82f6" stopOpacity="1" />
                          <stop offset="100%" stopColor="#10b981" />
                        </linearGradient>
                      </defs>

                      {/* Gridlines */}
                      <line x1="0" y1="40" x2="500" y2="40" stroke="#f1f5f9" strokeWidth="1" strokeDasharray="4 4" />
                      <line x1="0" y1="90" x2="500" y2="90" stroke="#f1f5f9" strokeWidth="1" strokeDasharray="4 4" />
                      <line x1="0" y1="140" x2="500" y2="140" stroke="#f1f5f9" strokeWidth="1" strokeDasharray="4 4" />

                      {/* Wave Fill Area */}
                      <path d="M 0 170 Q 100 130 200 90 T 400 60 T 500 40 L 500 170 L 0 170 Z" fill="url(#areaGradient)" />

                      {/* Wave Line */}
                      <path d="M 0 170 Q 100 130 200 90 T 400 60 T 500 40" fill="transparent" stroke="url(#strokeGradient)" strokeWidth="3" strokeLinecap="round" />

                      {/* Points */}
                      <circle cx="100" cy="150" r="5" fill="#6366f1" stroke="#ffffff" strokeWidth="1.5" />
                      <circle cx="200" cy="90" r="5" fill="#3b82f6" stroke="#ffffff" strokeWidth="1.5" />
                      <circle cx="300" cy="75" r="5" fill="#3b82f6" stroke="#ffffff" strokeWidth="1.5" />
                      <circle cx="400" cy="60" r="5" fill="#10b981" stroke="#ffffff" strokeWidth="1.5" />
                      <circle cx="500" cy="40" r="5" fill="#10b981" stroke="#ffffff" strokeWidth="1.5" />
                    </svg>
                    
                     {/* Floating Tooltips */}
                     <div className="absolute top-[135px] left-[70px] bg-slate-900/90 text-white text-[9px] font-bold px-1.5 py-0.5 rounded font-mono shadow">₹39,200</div>
                     <div className="absolute top-[75px] left-[180px] bg-slate-900/90 text-white text-[9px] font-bold px-1.5 py-0.5 rounded font-mono shadow">₹78,400</div>
                     <div className="absolute top-[45px] left-[280px] bg-slate-900/90 text-white text-[9px] font-bold px-1.5 py-0.5 rounded font-mono shadow">₹1,16,000</div>
                     <div className="absolute top-[20px] left-[370px] bg-slate-900/90 text-white text-[9px] font-bold px-1.5 py-0.5 rounded font-mono shadow">₹{calculateMRR().toLocaleString('en-IN')}</div>
                  </div>

                  {/* Horizontal Labels */}
                  <div className="flex justify-between text-[10px] text-slate-400 font-bold font-mono px-2 mt-4">
                    <span>Dec</span>
                    <span>Jan</span>
                    <span>Feb</span>
                    <span>Mar</span>
                    <span>Apr</span>
                    <span>May (Current)</span>
                  </div>
                </div>

                {/* 2. SVG Bar Chart for Subscription Segments (takes 1/3 cols) */}
                <div className="bg-white border border-blue-100 rounded-2xl p-6 shadow-sm flex flex-col justify-between">
                  <div className="w-full flex justify-between items-center mb-6 border-b border-gray-50 pb-3">
                    <div>
                      <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                        <Sparkles className="w-4 h-4 text-indigo-500" /> Plan Breakdown
                      </h3>
                      <p className="text-[10px] text-slate-400 font-medium">Distribution by active product tier</p>
                    </div>
                  </div>

                  <div className="space-y-4 my-2 flex-1 flex flex-col justify-center">
                    {[
                      { plan: 'Enterprise', count: clients.filter(c => c.plan === 'Enterprise').length, color: 'bg-emerald-500' },
                      { plan: 'Growth', count: clients.filter(c => c.plan === 'Growth').length, color: 'bg-blue-500' },
                      { plan: 'Starter', count: clients.filter(c => c.plan === 'Starter').length, color: 'bg-indigo-500' },
                      { plan: 'Unassigned', count: clients.filter(c => c.plan === 'Unassigned').length, color: 'bg-slate-300' }
                    ].map((seg, idx) => {
                      const pct = clients.length > 0 ? (seg.count / clients.length) * 100 : 0;
                      return (
                        <div key={idx} className="space-y-1">
                          <div className="flex justify-between text-xs font-semibold">
                            <span className="text-slate-700">{seg.plan} Plan</span>
                            <span className="text-slate-500 font-mono">{seg.count} ({Math.round(pct)}%)</span>
                          </div>
                          <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                            <div className={`h-full ${seg.color} rounded-full transition-all duration-1000`} style={{ width: `${pct}%` }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-[11px] text-slate-400 font-medium text-center border-t border-gray-50 pt-2.5 mt-2">
                    Segmentation breakdown across all operating channels.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: MANAGE DISTRIBUTORS */}
          {saTab === 'distributors' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fadeIn">
              {/* Provision form */}
              <div className="bg-white border border-blue-100 rounded-2xl p-6 shadow-sm h-fit">
                <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <PlusCircle className="w-5 h-5 text-blue-600" /> Provision New Distributor
                </h2>
                <p className="text-xs text-slate-500 mb-6 font-semibold">
                  Create a regional distributor partner account. They will be granted an authorization license to spawn client shops and assign plans.
                </p>
                
                <form onSubmit={handleCreateDistributor} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Distributor Name</label>
                    <input
                      type="text"
                      required
                      value={newDistName}
                      onChange={(e) => setNewDistName(e.target.value)}
                      placeholder="e.g., Nexus Distribution Corp"
                      className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Primary Contact Email</label>
                    <input
                      type="email"
                      required
                      value={newDistEmail}
                      onChange={(e) => setNewDistEmail(e.target.value)}
                      placeholder="e.g., contact@nexusdist.com"
                      className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Login Username</label>
                      <input
                        type="text"
                        required
                        value={newDistUsername}
                        onChange={(e) => setNewDistUsername(e.target.value)}
                        placeholder="e.g., nexus_dist"
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Login Password</label>
                      <input
                        type="password"
                        required
                        value={newDistPassword}
                        onChange={(e) => setNewDistPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Operating Region</label>
                      <input
                        type="text"
                        value={newDistRegion}
                        onChange={(e) => setNewDistRegion(e.target.value)}
                        placeholder="e.g., North America"
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Max Clients Limit</label>
                      <input
                        type="number"
                        min="1"
                        value={newDistMaxClients}
                        onChange={(e) => setNewDistMaxClients(e.target.value)}
                        placeholder="e.g., 5"
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full mt-4 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-bold py-3 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 border border-blue-400/20 shadow-sm"
                  >
                    <Plus className="w-4 h-4" /> Provision Distributor Account
                  </button>
                </form>
              </div>

              {/* Distributors list table */}
              <div className="lg:col-span-2 bg-white border border-blue-100 rounded-2xl p-6 shadow-sm flex flex-col">
                <h2 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" /> Active Regional Distributors List
                </h2>
                <p className="text-xs text-slate-500 mb-6 font-semibold">
                  Registered regional agents operating under your license permissions.
                </p>

                <div className="overflow-x-auto border border-gray-200 rounded-xl bg-white">
                  <table className="min-w-full divide-y divide-gray-150">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Distributor</th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Contact Email</th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Region</th>
                        <th className="px-5 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Licenses</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-sm font-medium">
                      {distributors.map(dist => {
                        const myClientsCount = clients.filter(c => c.distributorId === dist.id).length;
                        return (
                          <tr key={dist.id} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-5 py-3.5">
                              <span className="font-extrabold text-slate-900 block">{dist.name}</span>
                              <span className="text-[10px] text-slate-400 font-mono leading-none">ID: {dist.id}</span>
                            </td>
                            <td className="px-5 py-3.5 text-slate-700">{dist.email}</td>
                            <td className="px-5 py-3.5">
                              <span className="bg-blue-50 text-blue-850 text-xs px-2.5 py-0.5 rounded-full font-bold border border-blue-200">
                                {dist.region}
                              </span>
                            </td>
                            <td className="px-5 py-3.5 text-center">
                              <span className="font-bold text-slate-800">{myClientsCount}</span>
                              <span className="text-slate-400 text-xs"> / {dist.maxClients} max</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: FINANCIAL REPORTS & FILTER WORKFLOW */}
          {saTab === 'reports' && (() => {
            // Apply reports filter matching
            const filteredClients = clients.filter(c => {
              // Plan Filter
              if (planFilter !== 'all') {
                if (c.plan !== planFilter) return false;
              }
              // Distributor Filter
              if (distributorFilter !== 'all') {
                if (c.distributorId !== distributorFilter) return false;
              }
              // Status Filter
              if (statusFilter !== 'all') {
                const isExpired = c.isExpired || c.id === 'expired_client' || !c.plan || c.plan === 'Unassigned';
                if (statusFilter === 'expired') {
                  if (!isExpired) return false;
                } else if (statusFilter === 'active') {
                  if (isExpired) return false;
                }
              }
              return true;
            });

            // Compute Stats
            const totalClients = filteredClients.length;
            const totalMRR = filteredClients.reduce((sum, c) => {
              if (c.plan) {
                if (plans[c.plan]) return sum + Number(plans[c.plan].price);
                if (c.plan === 'Enterprise') return sum + 249;
                if (c.plan === 'Growth') return sum + 99;
                if (c.plan === 'Starter') return sum + 49;
              }
              return sum;
            }, 0);
            const avgPlanPrice = totalClients > 0 ? (totalMRR / totalClients) : 0;

            const starterCount = filteredClients.filter(c => c.plan === 'Starter').length;
            const growthCount = filteredClients.filter(c => c.plan === 'Growth').length;
            const enterpriseCount = filteredClients.filter(c => c.plan === 'Enterprise').length;
            const unassignedCount = filteredClients.filter(c => !c.plan || c.plan === 'Unassigned').length;

            return (
              <div className="space-y-6 animate-fadeIn">
                {/* Reports Filter Bar */}
                <div className="bg-white border border-blue-100 rounded-2xl p-5 shadow-sm space-y-4">
                  <div className="flex items-center gap-2 pb-3 border-b border-blue-50">
                    <Filter className="w-5 h-5 text-blue-600" />
                    <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wide">Audit & Financial Filters</h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Plan Selector */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">SaaS Plan Tier</label>
                      <select
                        value={planFilter}
                        onChange={(e) => setPlanFilter(e.target.value)}
                        className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-xs text-slate-800 font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                      >
                        <option value="all">All Plans (Starter, Growth, Enterprise)</option>
                        <option value="Starter">Starter Plan</option>
                        <option value="Growth">Growth Plan</option>
                        <option value="Enterprise">Enterprise Plan</option>
                        <option value="Unassigned">Unassigned / Pending</option>
                      </select>
                    </div>

                    {/* Distributor Selector */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Managing Distributor Channel</label>
                      <select
                        value={distributorFilter}
                        onChange={(e) => setDistributorFilter(e.target.value)}
                        className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-xs text-slate-800 font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                      >
                        <option value="all">All Distributor Channels</option>
                        {distributors.map(d => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                        <option value="Direct Signup">Direct / Self Signup Gateway</option>
                      </select>
                    </div>

                    {/* Account Status Selector */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">License Expiration Status</label>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-xs text-slate-800 font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                      >
                        <option value="all">All Expiration States</option>
                        <option value="active">Active & Subscribed Tiers</option>
                        <option value="expired">Expired or Locked Licenses</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Filtered Statistics */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-sm">
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">Filtered Tenants</span>
                    <span className="text-3xl font-extrabold text-slate-900 mt-1 block">{totalClients} Shops</span>
                    <span className="text-xs text-slate-500 font-medium block mt-1.5">Matching criteria parameters</span>
                  </div>
                  <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-sm">
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">MRR Contribution</span>
                    <span className="text-3xl font-extrabold text-emerald-600 mt-1 block">₹{totalMRR} INR</span>
                    <span className="text-xs text-slate-500 font-medium block mt-1.5">Sum of filtered active licenses</span>
                  </div>
                  <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-sm">
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">Avg Subscription Value</span>
                    <span className="text-3xl font-extrabold text-slate-900 mt-1 block">₹{avgPlanPrice.toFixed(1)} INR</span>
                    <span className="text-xs text-slate-500 font-medium block mt-1.5">ARPU across matched shops</span>
                  </div>
                  <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-sm flex flex-col justify-between">
                    <div>
                      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">Plan Breakdown Share</span>
                      <div className="flex items-center gap-1.5 mt-2 h-4 w-full bg-gray-150 rounded overflow-hidden">
                        <div style={{ width: `${totalClients > 0 ? (starterCount / totalClients) * 100 : 0}%` }} className="bg-blue-500 h-full" title="Starter" />
                        <div style={{ width: `${totalClients > 0 ? (growthCount / totalClients) * 100 : 0}%` }} className="bg-blue-500 h-full" title="Growth" />
                        <div style={{ width: `${totalClients > 0 ? (enterpriseCount / totalClients) * 100 : 0}%` }} className="bg-emerald-500 h-full" title="Enterprise" />
                        <div style={{ width: `${totalClients > 0 ? (unassignedCount / totalClients) * 100 : 0}%` }} className="bg-gray-400 h-full" title="Unassigned" />
                      </div>
                    </div>
                    <span className="text-[9px] text-slate-400 font-bold block leading-none">Orange: Starter | Blue: Growth | Green: Enterprise</span>
                  </div>
                </div>

                {/* Filtered Clients Data Sheet */}
                <div className="bg-white border border-blue-100 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="font-extrabold text-slate-950 text-base">Filtered Platform Clients Data Sheet</h4>
                      <p className="text-xs text-slate-500 mt-0.5 font-semibold">Audit-ready list formatted for administrative compliance exports.</p>
                    </div>
                    <button
                      onClick={() => {
                        toast.success('Reports CSV details copied to administrative clipboard!', { icon: '📋' });
                      }}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-colors flex items-center gap-1.5 shadow-sm"
                    >
                      <Download className="w-3.5 h-3.5" /> Export Audit Log
                    </button>
                  </div>

                  <div className="overflow-x-auto border border-gray-200 rounded-xl bg-white">
                    <table className="min-w-full divide-y divide-gray-150">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Client Business</th>
                          <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Channel distributor</th>
                          <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">License plan</th>
                          <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Assigned Date</th>
                          <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Next Billing</th>
                          <th className="px-5 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Plan Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 text-sm font-medium">
                        {filteredClients.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="px-5 py-8 text-center text-slate-400 italic">No client shops match the active filter criteria.</td>
                          </tr>
                        ) : (
                          filteredClients.map(c => {
                            const isExpired = c.isExpired || c.id === 'expired_client' || !c.plan || c.plan === 'Unassigned';
                            return (
                              <tr key={c.id} className="hover:bg-blue-50/5 transition-colors">
                                <td className="px-5 py-3.5">
                                  <span className="font-extrabold text-slate-900 block">{c.shopName}</span>
                                  <span className="text-[10px] text-slate-400 block font-mono font-bold">Owner: {c.name} | Phone: {c.phone}</span>
                                </td>
                                <td className="px-5 py-3.5 text-slate-700">
                                  {distributors.find(d => d.id === c.distributorId)?.name || c.distributorId}
                                </td>
                                <td className="px-5 py-3.5">
                                  <span className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-full border ${
                                    c.plan === 'Enterprise' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                    c.plan === 'Growth' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                    c.plan === 'Starter' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                    'bg-gray-100 text-gray-500 border border-gray-200'
                                  }`}>
                                    {c.plan}
                                  </span>
                                </td>
                                <td className="px-5 py-3.5 font-mono text-xs text-slate-600">{c.assignedDate || '--'}</td>
                                <td className="px-5 py-3.5 font-mono text-xs text-slate-600">{c.nextBilling || '--'}</td>
                                <td className="px-5 py-3.5 text-center">
                                  {isExpired ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-red-50 text-red-700 border border-red-200">
                                      Expired / Locked
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-green-50 text-green-700 border border-green-200">
                                      Active
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Real-time Platform Billing & UPI Checkout Ledger */}
                <div className="bg-white border border-blue-100 rounded-2xl p-6 shadow-sm mt-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-100 pb-4 mb-4 gap-3">
                    <div>
                      <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <Coins className="w-5 h-5 text-indigo-500 animate-bounce" /> Real-time Platform Billing & GPay Transaction Ledger
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Verified audit trail of customer subscription checkouts and direct payments.
                      </p>
                    </div>
                    <span className="bg-indigo-50 text-indigo-700 text-xs px-2.5 py-1 rounded-full font-bold border border-indigo-200">
                      Total: {logs.length} Transactions
                    </span>
                  </div>

                  <div className="overflow-x-auto border border-gray-200 rounded-xl bg-slate-50/30">
                    <table className="min-w-full divide-y divide-gray-150">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Timestamp</th>
                          <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Gateway / Agent</th>
                          <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Subscribing Tenant</th>
                          <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Plan Assigned</th>
                          <th className="px-5 py-3 text-right text-xs font-bold text-slate-600 uppercase tracking-wider">Amount Received</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white text-sm">
                        {logs.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="px-5 py-8 text-center text-slate-400 italic">No transactions logged on the system yet.</td>
                          </tr>
                        ) : (
                          logs.map(log => {
                            const isGPay = log.distributorName.includes('Direct Signup') || log.distributorName.includes('Gateway');
                            let exactINR = 49;
                            const planObj = plans[log.planName];
                            if (planObj) {
                              exactINR = Number(planObj.price);
                            } else if (log.price) {
                              exactINR = Number(log.price);
                            } else {
                              exactINR = log.planName === 'Enterprise' ? 19999 : (log.planName === 'Growth' ? 7999 : 3999);
                            }
                            return (
                              <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                                <td className="px-5 py-3 text-xs text-slate-500 font-mono">
                                  {new Date(log.timestamp).toLocaleString('en-IN')}
                                </td>
                                <td className="px-5 py-3">
                                  {isGPay ? (
                                    <span className="inline-flex items-center gap-1 bg-[#E8F0FE] text-[#1A73E8] border border-[#ADCCF9] text-[10px] font-extrabold px-2 py-0.5 rounded-full shadow-sm">
                                      <span className="w-1.5 h-1.5 rounded-full bg-[#EA4335] animate-ping" /> GPay / UPI Checkout
                                    </span>
                                  ) : (
                                    <div className="flex flex-col">
                                      <span className="text-[10px] font-extrabold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full w-max shadow-sm">
                                        Distributor Channel
                                      </span>
                                      <span className="text-xs text-slate-500 mt-1 font-medium">{log.distributorName}</span>
                                    </div>
                                  )}
                                </td>
                                <td className="px-5 py-3 text-slate-800 font-bold text-sm">
                                  {log.clientName}
                                </td>
                                <td className="px-5 py-3">
                                  <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full border ${
                                    log.planName === 'Enterprise' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                    log.planName === 'Growth' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                    'bg-blue-50 text-blue-700 border-blue-200'
                                  }`}>
                                    {log.planName}
                                  </span>
                                </td>
                                <td className="px-5 py-3 text-right text-slate-900 font-extrabold font-mono text-sm">
                                  ₹{exactINR.toLocaleString('en-IN')}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* TAB 4: SAAS SUBSCRIPTION PLANS */}
          {saTab === 'plans' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fadeIn">
              {/* Left Column: Active Plans List */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white border border-blue-100 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                        <Coins className="w-5 h-5 text-blue-600 animate-spin animate-spin-slow" /> Active SaaS Subscription Plans
                      </h2>
                      <p className="text-xs text-slate-500 mt-0.5">
                        These plans govern features and restrictions in real time across the multi-tenant system.
                      </p>
                    </div>
                    <span className="bg-blue-100 text-blue-700 text-xs px-2.5 py-1 rounded-full font-semibold border border-blue-200">
                      {Object.keys(plans).length} Active Tiers
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.keys(plans).map((planKey) => {
                      const p = plans[planKey];
                      return (
                        <div key={planKey} className="bg-blue-50/20 border border-blue-100 hover:border-blue-300 rounded-2xl p-5 shadow-sm transition-all relative overflow-hidden group flex flex-col justify-between min-h-[240px]">
                          <div>
                            {/* Price Tag */}
                            <div className="absolute top-4 right-4 text-right">
                              <span className="text-2xl font-black text-blue-600 font-mono">₹{p.price}</span>
                              <span className="text-[10px] text-slate-400 block font-bold font-sans uppercase">{p.cycle}</span>
                            </div>

                            <h3 className="font-extrabold text-slate-800 text-base">{p.name}</h3>
                            <p className="text-slate-500 text-xs mt-1.5 leading-relaxed pr-20 min-h-[40px]">{p.description}</p>
                            <div className="flex items-center gap-1.5 mt-2">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Branches:</span>
                              <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full border ${
                                p.branchLimit === 'unlimited' || !p.branchLimit
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  : 'bg-amber-50 text-amber-700 border-amber-200'
                              }`}>
                                {p.branchLimit === 'unlimited' || !p.branchLimit ? 'Unlimited' : `Max ${p.branchLimit}`}
                              </span>
                              {p.branchLimit !== 'unlimited' && p.branchLimit && (
                                <span className="text-[10px] text-slate-400 font-semibold">+₹{p.additionalBranchPrice || 99}/extra</span>
                              )}
                            </div>
                            
                            <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                              <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider block">Allowed Sidebar Elements:</span>
                              <div className="flex flex-wrap gap-1.5 max-h-[100px] overflow-y-auto pr-1">
                                {(p.allowedFeatures || ['Dashboard', 'Jobs', 'Customers']).map((feat, i) => (
                                  <span key={i} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
                                    <Check className="w-2.5 h-2.5" /> {feat}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Edit / Delete Buttons Panel */}
                          <div className="mt-4 pt-3 border-t border-dashed border-slate-200/50 flex items-center justify-end gap-2 shrink-0">
                            <button
                              type="button"
                              onClick={() => handleStartEditPlan(planKey)}
                              className="px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 hover:text-blue-800 font-extrabold rounded-lg text-[10px] transition-colors flex items-center gap-1 shadow-sm border border-blue-200/25 cursor-pointer"
                            >
                              <Pencil className="w-3 h-3" /> Edit
                            </button>
                            {planKey !== 'Starter' && planKey !== 'Growth' && planKey !== 'Enterprise' ? (
                              <button
                                type="button"
                                onClick={() => handleDeletePlan(planKey)}
                                className="px-2.5 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 hover:text-red-800 font-extrabold rounded-lg text-[10px] transition-colors flex items-center gap-1 shadow-sm border border-red-200/25 cursor-pointer"
                              >
                                <Trash2 className="w-3 h-3" /> Delete
                              </button>
                            ) : (
                              <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider px-2 py-1 select-none">
                                Core Plan
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Right Column: Define New Plan Form */}
              <div className="bg-white border border-blue-100 rounded-2xl p-6 shadow-sm relative overflow-hidden flex flex-col justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
                    {editingPlanKey ? (
                      <>
                        <Pencil className="w-5 h-5 text-blue-600 animate-pulse" /> Edit SaaS Plan
                      </>
                    ) : (
                      <>
                        <PlusCircle className="w-5 h-5 text-blue-600" /> Create Custom Plan
                      </>
                    )}
                  </h2>
                  <p className="text-xs text-slate-500 mb-6">
                    {editingPlanKey 
                      ? `Modifying pricing, description, and permissions for the custom "${plans[editingPlanKey]?.name}" package.`
                      : 'Define a brand new SaaS pricing package and customize its feature permissions.'
                    }
                  </p>

                  <form onSubmit={handleCreatePlan} className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Plan Name *</label>
                      <input
                        type="text"
                        required
                        value={newPlanName}
                        onChange={(e) => setNewPlanName(e.target.value)}
                        placeholder="e.g., Lite, Pro, Premium Plus"
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Price INR (₹) *</label>
                        <input
                          type="number"
                          required
                          min="0"
                          value={newPlanPrice}
                          onChange={(e) => setNewPlanPrice(Number(e.target.value))}
                          placeholder="4999"
                          className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-950 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Cycle *</label>
                        <select
                          value={newPlanCycle}
                          onChange={(e) => setNewPlanCycle(e.target.value)}
                          className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-950 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="Monthly">Monthly</option>
                          <option value="Yearly">Yearly</option>
                        </select>
                      </div>
                    </div>

                    {/* Branch Allocation Section */}
                    <div className="bg-amber-50/60 border border-amber-200/60 rounded-xl p-3.5 space-y-3">
                      <label className="block text-xs font-bold text-amber-800 uppercase tracking-wider">Branch Allocation</label>
                      <div className="flex flex-col gap-2">
                        <label className="flex items-center gap-2.5 cursor-pointer">
                          <input
                            type="radio"
                            name="branchLimitType"
                            value="unlimited"
                            checked={newPlanBranchLimit === 'unlimited'}
                            onChange={() => setNewPlanBranchLimit('unlimited')}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                          />
                          <span className="text-xs font-semibold text-slate-700">No Restriction — Unlimited branches</span>
                        </label>
                        <label className="flex items-center gap-2.5 cursor-pointer">
                          <input
                            type="radio"
                            name="branchLimitType"
                            value="limited"
                            checked={newPlanBranchLimit !== 'unlimited'}
                            onChange={() => setNewPlanBranchLimit('2')}
                            className="w-4 h-4 text-amber-600 border-gray-300 focus:ring-amber-500 cursor-pointer"
                          />
                          <span className="text-xs font-semibold text-slate-700">Restrict to</span>
                          <input
                            type="number"
                            min="1"
                            max="100"
                            disabled={newPlanBranchLimit === 'unlimited'}
                            value={newPlanBranchLimit === 'unlimited' ? '' : newPlanBranchLimit}
                            onChange={(e) => setNewPlanBranchLimit(e.target.value || '2')}
                            onClick={() => { if (newPlanBranchLimit === 'unlimited') setNewPlanBranchLimit('2'); }}
                            placeholder="2"
                            className="w-16 bg-white border border-amber-300 rounded-lg px-2 py-1 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-40"
                          />
                          <span className="text-xs font-semibold text-slate-700">branches max</span>
                        </label>
                      </div>
                      {newPlanBranchLimit !== 'unlimited' && (
                        <div className="pt-1">
                          <label className="block text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-1.5">Price per Extra Branch (₹)</label>
                          <input
                            type="number"
                            min="0"
                            value={newPlanAdditionalBranchPrice}
                            onChange={(e) => setNewPlanAdditionalBranchPrice(Number(e.target.value))}
                            placeholder="99"
                            className="w-full bg-white border border-amber-300 rounded-xl px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
                          />
                          <p className="text-[10px] text-amber-600 mt-1">Customer pays this one-time amount to unlock each additional branch beyond the base limit.</p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Short Description</label>
                      <input
                        type="text"
                        value={newPlanDesc}
                        onChange={(e) => setNewPlanDesc(e.target.value)}
                        placeholder="e.g. Best package for mid-scale garages"
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-950 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2.5">
                        Allowed Sidebar Modules (Tick boxes):
                      </label>
                      <div className="grid grid-cols-2 gap-2 bg-blue-50/20 border border-blue-50 p-3.5 rounded-xl max-h-[160px] overflow-y-auto">
                        {[
                          'Dashboard', 'Jobs', 'Customers', 'Products', 'Inventory', 'Expenses', 'Statement', 'Reports', 'Settings'
                        ].map((module) => {
                          const isChecked = newPlanFeatures.includes(module);
                          return (
                            <label key={module} className="flex items-center gap-2 cursor-pointer text-xs text-slate-700 hover:text-slate-900">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleToggleFeature(module)}
                                className="w-4 h-4 rounded text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                              />
                              <span className="font-semibold">{module}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div className="flex gap-2 pt-2">
                      {editingPlanKey && (
                        <button
                          type="button"
                          onClick={handleCancelEditPlan}
                          className="w-1/3 bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-700 font-extrabold py-3 rounded-xl transition-all duration-200 text-sm border border-gray-200 cursor-pointer"
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        type="submit"
                        className={`font-bold py-3 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 border shadow-sm cursor-pointer ${
                          editingPlanKey 
                            ? 'w-2/3 bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-500/10'
                            : 'w-full bg-blue-600 hover:bg-blue-500 text-white border-blue-400/20'
                        }`}
                      >
                        {editingPlanKey ? (
                          <>
                            <Check className="w-4 h-4" /> Save Updates
                          </>
                        ) : (
                          <>
                            <Plus className="w-4 h-4" /> Create Plan
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* TAB 5: MANAGE CLIENTS / CUSTOMERS */}
          {saTab === 'clients' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fadeIn">
              {/* Client Form (Left Column) */}
              <div className="bg-white border border-blue-100 rounded-2xl p-6 shadow-sm h-fit">
                <h2 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
                  {editingClientId ? (
                    <>
                      <Pencil className="w-5 h-5 text-blue-600" /> Edit Customer Details
                    </>
                  ) : (
                    <>
                      <PlusCircle className="w-5 h-5 text-blue-600" /> Manually Add Customer
                    </>
                  )}
                </h2>
                <p className="text-xs text-slate-500 mb-6 font-semibold">
                  {editingClientId 
                    ? 'Modify the customer shop settings, plans, login details or address info.' 
                    : 'Register and provision a new manual customer tenant straight from Super Admin.'}
                </p>

                <form onSubmit={handleCreateOrUpdateClientByAdmin} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Shop / Company Name</label>
                    <input
                      type="text"
                      required
                      value={newClientShop}
                      onChange={(e) => setNewClientShop(e.target.value)}
                      placeholder="e.g., Apex Repair Center"
                      className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Owner / Contact Name</label>
                    <input
                      type="text"
                      required
                      value={newClientName}
                      onChange={(e) => setNewClientName(e.target.value)}
                      placeholder="e.g., Satish Kumar"
                      className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Phone Number</label>
                      <input
                        type="text"
                        required
                        value={newClientPhone}
                        onChange={(e) => setNewClientPhone(e.target.value)}
                        placeholder="e.g., 9876543210"
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Email Address</label>
                      <input
                        type="email"
                        value={newClientEmail}
                        onChange={(e) => setNewClientEmail(e.target.value)}
                        placeholder="e.g., client@example.com"
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Login Username</label>
                      <input
                        type="text"
                        required
                        value={newClientUsername}
                        onChange={(e) => setNewClientUsername(e.target.value)}
                        placeholder="e.g., apex_admin"
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                        {editingClientId ? 'Password (leave blank to keep)' : 'Login Password'}
                      </label>
                      <input
                        type="password"
                        required={!editingClientId}
                        value={newClientPassword}
                        onChange={(e) => setNewClientPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">SaaS Plan</label>
                      <select
                        value={newClientPlanId}
                        onChange={(e) => setNewClientPlanId(e.target.value)}
                        className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Unassigned (Expired/Locked)</option>
                        {Object.values(plans).map(p => (
                          <option key={p.id} value={p.id}>{p.name} - ₹{p.price}/{p.cycle}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Distributor</label>
                      <select
                        value={newClientDistributorId}
                        onChange={(e) => setNewClientDistributorId(e.target.value)}
                        className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Direct (No Distributor)</option>
                        {distributors.map(d => (
                          <option key={d.id} value={d.id}>{d.name} ({d.region})</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">GPay / Offline Payment UTR #</label>
                    <input
                      type="text"
                      value={newClientUtr}
                      onChange={(e) => setNewClientUtr(e.target.value)}
                      placeholder="e.g., 428901238910 or GPay Ref #"
                      className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                    />
                  </div>

                  <div className="border-t border-gray-100 pt-4 space-y-4">
                    <h3 className="text-xs font-bold text-slate-800 uppercase tracking-widest">Address & Billing Details</h3>
                    
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Street Address</label>
                      <input
                        type="text"
                        value={newClientAddress}
                        onChange={(e) => setNewClientAddress(e.target.value)}
                        placeholder="e.g., 123 Business Park"
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">City</label>
                        <input
                          type="text"
                          value={newClientCity}
                          onChange={(e) => setNewClientCity(e.target.value)}
                          placeholder="Delhi"
                          className="w-full bg-white border border-gray-300 rounded-xl px-2 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">State</label>
                        <input
                          type="text"
                          value={newClientState}
                          onChange={(e) => setNewClientState(e.target.value)}
                          placeholder="Delhi"
                          className="w-full bg-white border border-gray-300 rounded-xl px-2 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Pincode</label>
                        <input
                          type="text"
                          value={newClientPincode}
                          onChange={(e) => setNewClientPincode(e.target.value)}
                          placeholder="110001"
                          className="w-full bg-white border border-gray-300 rounded-xl px-2 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">WhatsApp No</label>
                        <input
                          type="text"
                          value={newClientWhatsapp}
                          onChange={(e) => setNewClientWhatsapp(e.target.value)}
                          placeholder="WhatsApp number"
                          className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">GST Registration No</label>
                        <input
                          type="text"
                          value={newClientGst}
                          onChange={(e) => setNewClientGst(e.target.value)}
                          placeholder="GSTIN"
                          className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="submit"
                      className="flex-1 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-bold py-2.5 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-sm text-sm"
                    >
                      {editingClientId ? 'Save Updates' : 'Provision Customer'}
                    </button>
                    {editingClientId && (
                      <button
                        type="button"
                        onClick={handleCancelClientEdit}
                        className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold px-4 py-2.5 rounded-xl transition-all text-sm animate-fadeIn"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>

              {/* Client List (Right Column) */}
              <div className="lg:col-span-2 bg-white border border-blue-100 rounded-2xl p-6 shadow-sm flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-blue-600" /> Register / Manage Customers List
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5 font-semibold">
                      Control and manage SaaS tenants, lock or activate accounts, change subscriptions.
                    </p>
                  </div>
                  <span className="bg-blue-100 text-blue-700 text-xs px-2.5 py-1 rounded-full font-semibold border border-blue-200">
                    Total: {clients.length} customers
                  </span>
                </div>

                <div className="overflow-x-auto border border-gray-200 rounded-xl bg-white flex-1">
                  <table className="min-w-full divide-y divide-gray-150">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Business / Shop</th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Admin Contact</th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">SaaS Subscription</th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Distributor</th>
                        <th className="px-5 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Status</th>
                        <th className="px-5 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-sm">
                      {clients.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-5 py-8 text-center text-slate-400 italic">No customers registered in the database.</td>
                        </tr>
                      ) : (
                        clients.map(c => (
                          <tr key={c.id} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-5 py-3.5">
                              <div className="font-extrabold text-slate-900">{c.shopName}</div>
                              <div className="text-[10px] text-slate-400 font-mono mt-0.5 leading-none">ID: {c.id}</div>
                              {c.utr && (
                                <div className="text-[10px] text-emerald-600 font-bold mt-1 font-mono">
                                  UTR: {c.utr}
                                </div>
                              )}
                            </td>
                            <td className="px-5 py-3.5">
                              <div className="text-slate-800 font-bold">{c.name}</div>
                              <div className="text-[11px] text-slate-500 font-medium">
                                Username: <span className="font-semibold text-indigo-650">{c.username}</span>
                                <span className="ml-2 pl-2 border-l border-slate-200">
                                  Password: <span className="font-bold text-rose-600 font-mono">{c.plainPassword || '••••••••'}</span>
                                </span>
                              </div>
                              <div className="text-[11px] text-slate-500 mt-0.5">{c.phone} {c.email ? `| ${c.email}` : ''}</div>
                            </td>
                            <td className="px-5 py-3.5">
                              {c.plan ? (
                                <div className="space-y-1">
                                  <span className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-full border ${
                                    c.plan === 'Enterprise' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                    c.plan === 'Growth' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                    'bg-indigo-50 text-indigo-700 border-indigo-200'
                                  }`}>
                                    {c.plan}
                                  </span>
                                  <div className="text-[10px] text-slate-400 font-mono">
                                    Next: {c.nextBilling ? new Date(c.nextBilling).toLocaleDateString() : '--'}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-[10px] font-extrabold px-2.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
                                  Unassigned
                                </span>
                              )}
                            </td>
                            <td className="px-5 py-3.5">
                              {c.distributorName ? (
                                <span className="font-bold text-slate-850 text-xs">{c.distributorName}</span>
                              ) : (
                                <span className="text-slate-400 italic text-xs font-semibold">Direct Online</span>
                              )}
                            </td>
                            <td className="px-5 py-3.5 text-center">
                              <button
                                onClick={() => handleToggleClientStatus(c.id)}
                                className={`text-[10px] font-extrabold px-2.5 py-1 rounded-full border shadow-sm transition-all active:scale-95 flex items-center gap-1.5 mx-auto ${
                                  c.isActive 
                                    ? 'bg-emerald-50 text-emerald-750 border-emerald-250 hover:bg-emerald-100' 
                                    : 'bg-rose-50 text-rose-750 border-rose-250 hover:bg-rose-100'
                                }`}
                              >
                                {c.isActive ? (
                                  <>
                                    <Check className="w-3 h-3 text-emerald-600" /> Active
                                  </>
                                ) : (
                                  <>
                                    <Lock className="w-3 h-3 text-rose-600" /> Locked
                                  </>
                                )}
                              </button>
                            </td>
                            <td className="px-5 py-3.5 text-center">
                              <div className="flex items-center justify-center gap-1.5">
                                <button
                                  onClick={() => handleOpenRenewModal(c)}
                                  className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white rounded-lg border border-emerald-500/20 text-xs font-bold transition-all shadow-sm flex items-center gap-1"
                                  title="Manual Renewal (GPay / Cash / UPI)"
                                >
                                  <RefreshCw className="w-3 h-3" />
                                  <span>Renew</span>
                                </button>
                                <button
                                  onClick={() => handleEditClient(c)}
                                  className="p-1.5 bg-slate-50 hover:bg-blue-50 text-slate-500 hover:text-blue-600 rounded-lg border border-slate-200 hover:border-blue-200 transition-colors shadow-sm"
                                  title="Edit Customer"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteClientPermanently(c.id, c.shopName)}
                                  className="p-1.5 bg-slate-50 hover:bg-red-50 text-slate-500 hover:text-red-650 rounded-lg border border-slate-200 hover:border-red-200 transition-colors shadow-sm"
                                  title="Permanently Delete Customer"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* -------------------------------------------------------------------------------------------------------------------------------- */}
      {/* 2. DISTRIBUTOR WORKSPACE (DEPRECATED DUPLICATE DISABLED) */}
      {/* -------------------------------------------------------------------------------------------------------------------------------- */}
      {false && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* LEFT & MID: CLIENTS LIST & CREATION (2 Columns on large screens) */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Create Client Form */}
            <div className="bg-white border border-blue-100 rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
                <PlusCircle className="w-5 h-5 text-blue-600" /> Create Client Shop Account
              </h2>
              <p className="text-xs text-slate-500 mb-6">
                Register a new retail or automotive shop client under your distributor tenancy.
              </p>

              <form onSubmit={handleCreateClient} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Owner / Admin Name</label>
                  <input
                    type="text"
                    required
                    value={newClientName}
                    onChange={(e) => setNewClientName(e.target.value)}
                    placeholder="e.g., Jane Doe"
                    className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Shop / Business Name</label>
                  <input
                    type="text"
                    required
                    value={newClientShop}
                    onChange={(e) => setNewClientShop(e.target.value)}
                    placeholder="e.g., Apex Mobile Repairs"
                    className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Primary Phone Number</label>
                  <input
                    type="text"
                    required
                    value={newClientPhone}
                    onChange={(e) => setNewClientPhone(e.target.value)}
                    placeholder="e.g., +1 555-0199"
                    className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Email Address</label>
                  <input
                    type="email"
                    value={newClientEmail}
                    onChange={(e) => setNewClientEmail(e.target.value)}
                    placeholder="e.g., contact@shop.com"
                    className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2 flex justify-end mt-2">
                  <button
                    type="submit"
                    className="w-full md:w-auto bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-bold px-6 py-3 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 border border-blue-400/20 shadow-sm"
                  >
                    <Plus className="w-4 h-4" /> Provision Client Portal
                  </button>
                </div>
              </form>
            </div>

            {/* Clients List under current Distributor */}
            <div className="bg-white border border-blue-100 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-600" /> Active Clients under your Scope
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Managing clients for <span className="text-blue-600 font-bold">{activeDistributor?.name}</span>
                  </p>
                </div>
                <span className="bg-blue-100 text-blue-700 text-xs px-2.5 py-1 rounded-full font-semibold border border-blue-200">
                  Total: {distributorClients.length} clients
                </span>
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded-xl bg-white">
                <table className="min-w-full divide-y divide-gray-150">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Client Business</th>
                      <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Store Admin</th>
                      <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Active SaaS Plan</th>
                      <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Next Billing</th>
                      <th className="px-5 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm">
                    {distributorClients.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-5 py-8 text-center text-slate-400 italic">No client shops registered under your distributor channel yet.</td>
                      </tr>
                    ) : (
                      distributorClients.map(c => (
                        <tr key={c.id} className="hover:bg-blue-50/10 transition-colors">
                          <td className="px-5 py-3">
                            <div className="font-bold text-slate-900 text-sm">{c.shopName}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5 font-mono">{c.phone}</div>
                          </td>
                          <td className="px-5 py-3">
                            <div className="text-slate-800 font-medium">{c.name}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5">{c.email}</div>
                          </td>
                          <td className="px-5 py-3">
                            <span className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-full border ${
                              c.plan === 'Enterprise' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              c.plan === 'Growth' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                              c.plan === 'Starter' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                              'bg-gray-100 text-gray-500 border border-gray-200'
                            }`}>
                              {c.plan}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-slate-600 font-mono text-xs">
                            {c.nextBilling ? c.nextBilling : '--'}
                          </td>
                          <td className="px-5 py-3 text-center">
                            <button
                              onClick={() => {
                                setAssistantClient(c.id);
                                setAssistantStep(2);
                                setAssistantPlan(c.plan !== 'Unassigned' ? c.plan : 'Starter');
                                // Scroll to Assistant on mobile
                                window.scrollTo({ top: document.getElementById('assistant-panel').offsetTop - 20, behavior: 'smooth' });
                              }}
                              className="bg-blue-50 hover:bg-blue-600 text-blue-700 hover:text-white border border-blue-200 text-xs font-bold px-3 py-1.5 rounded-lg transition-all shadow-sm"
                            >
                              Assign / Change Plan
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* RIGHT: INTERACTIVE SAAS PLAN ASSIGNMENT ASSISTANT WIDGET */}
          <div id="assistant-panel" className="space-y-6">
            <div className="bg-white border-2 border-blue-200 rounded-3xl p-6 shadow-md relative overflow-hidden flex flex-col justify-between min-h-[500px]">
              
              {/* Decorative Glow */}
              <div className="absolute right-0 top-0 w-32 h-32 bg-blue-500/5 rounded-full blur-2xl pointer-events-none" />
              <div className="absolute left-0 bottom-0 w-32 h-32 bg-indigo-500/5 rounded-full blur-2xl pointer-events-none" />

              <div>
                {/* Assistant Title Header */}
                <div className="flex items-center gap-3 border-b border-blue-100 pb-4 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md animate-pulse">
                    <Bot className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-slate-800 text-base flex items-center gap-1.5">
                      SaaS Plan Assistant <Sparkles className="w-4 h-4 text-blue-500" />
                    </h3>
                    <p className="text-[11px] text-blue-600 font-semibold">Assisting Distributor Plan Workflows</p>
                  </div>
                </div>

                {/* STEP 1: SELECT CLIENT */}
                {assistantStep === 1 && (
                  <div className="space-y-4 animate-fadeIn">
                    <div className="bg-blue-50/50 p-4 rounded-2xl border border-blue-100">
                      <p className="text-sm text-slate-700 leading-relaxed mb-3">
                        "Welcome! Let's activate a subscription license. First, select the client business you want to assign a billing plan to."
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Select Client Shop</label>
                      <select
                        value={assistantClient}
                        onChange={(e) => setAssistantClient(e.target.value)}
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">-- Choose registered shop --</option>
                        {distributorClients.map(c => (
                          <option key={c.id} value={c.id}>{c.shopName} ({c.name})</option>
                        ))}
                      </select>
                    </div>

                    <button
                      disabled={!assistantClient}
                      onClick={() => setAssistantStep(2)}
                      className={`w-full mt-4 font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 border shadow-sm ${
                        assistantClient 
                          ? 'bg-blue-600 hover:bg-blue-500 text-white border-blue-400/20' 
                          : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      }`}
                    >
                      Continue to Plan Selection <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* STEP 2: SELECT PLAN */}
                {assistantStep === 2 && (
                  <div className="space-y-4 animate-fadeIn">
                    <div className="bg-blue-50 p-3.5 rounded-xl border border-blue-100 text-xs mb-2">
                      <span className="text-slate-500">Client:</span> <strong className="text-slate-800">{clients.find(c => c.id === assistantClient)?.shopName}</strong>
                    </div>

                    <p className="text-sm text-slate-700">
                      "Excellent. Now choose which SaaS tier suits this client shop. Each tier unlocks a different feature set."
                    </p>

                    <div className="space-y-3 mt-4">
                      {Object.keys(PLANS).map(planKey => {
                        const plan = PLANS[planKey];
                        const isSelected = assistantPlan === planKey;
                        return (
                          <div
                            key={planKey}
                            onClick={() => setAssistantPlan(planKey)}
                            className={`p-3.5 rounded-xl border-2 cursor-pointer transition-all ${
                              isSelected 
                                ? 'bg-blue-50/50 border-blue-500 shadow-sm' 
                                : 'bg-white border-gray-200 hover:border-blue-200'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-bold text-slate-800">{plan.name}</span>
                              <span className="text-blue-600 font-mono font-bold">₹{plan.price} / mo</span>
                            </div>
                            <p className="text-[11px] text-slate-500 mt-1 leading-normal">{plan.description}</p>
                          </div>
                        );
                      })}
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <button
                        onClick={() => setAssistantStep(1)}
                        className="bg-gray-100 hover:bg-gray-200 text-slate-700 font-bold py-2.5 rounded-xl text-sm transition-all"
                      >
                        Back
                      </button>
                      <button
                        disabled={!assistantPlan}
                        onClick={() => setAssistantStep(3)}
                        className={`font-bold py-2.5 rounded-xl text-sm transition-all flex items-center justify-center gap-1 border shadow-sm ${
                          assistantPlan 
                            ? 'bg-blue-600 hover:bg-blue-500 text-white border-blue-400/20' 
                            : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        }`}
                      >
                        Verify Features <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* STEP 3: COMPARISON (ACTIVE & LOCKED FEATURES) */}
                {assistantStep === 3 && (
                  <div className="space-y-4 animate-fadeIn text-sm">
                    {/* Confirmation Check */}
                    <div className="bg-blue-50 p-3 rounded-xl border border-blue-100 flex justify-between items-center text-xs">
                      <div>
                        <span className="text-slate-500">Client:</span> <strong className="text-slate-800">{clients.find(c => c.id === assistantClient)?.shopName}</strong>
                      </div>
                      <div>
                        <span className="text-slate-500">Selected:</span> <strong className="text-blue-600">{assistantPlan}</strong>
                      </div>
                    </div>

                    <h4 className="font-bold text-slate-800 border-b border-blue-100 pb-1.5 flex items-center gap-2 text-xs text-blue-600 uppercase tracking-wider mt-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Active Features in this Plan:
                    </h4>
                    
                    <div className="max-h-[120px] overflow-y-auto space-y-1.5 bg-slate-50 p-2.5 rounded-lg border border-gray-100">
                      {PLANS[assistantPlan].features.map((feat, i) => (
                        <div key={i} className="flex items-start gap-2 text-[11px] text-slate-600">
                          <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                          <span>{feat}</span>
                        </div>
                      ))}
                    </div>

                    <h4 className="font-bold text-slate-800 border-b border-blue-100 pb-1.5 flex items-center gap-2 text-xs text-indigo-600 uppercase tracking-wider mt-3">
                      <Lock className="w-3.5 h-3.5 text-indigo-500" /> Locked Features (Upgrade Required):
                    </h4>

                    {PLANS[assistantPlan].locked.length === 0 ? (
                      <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg p-2 text-center text-[10px] font-bold">
                        All modules unlocked! (Enterprise package)
                      </div>
                    ) : (
                      <div className="max-h-[110px] overflow-y-auto space-y-1.5 bg-slate-50 p-2.5 rounded-lg border border-gray-100">
                        {PLANS[assistantPlan].locked.map((feat, i) => (
                          <div key={i} className="flex items-start gap-2 text-[11px] text-slate-400">
                            <span className="text-indigo-500 shrink-0 text-xs font-bold leading-none mt-0.5">🔒</span>
                            <span>{feat}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <button
                        onClick={() => setAssistantStep(2)}
                        className="bg-gray-100 hover:bg-gray-200 text-slate-700 font-bold py-2.5 rounded-xl text-sm transition-all"
                      >
                        Change Plan
                      </button>
                      <button
                        onClick={() => setAssistantStep(4)}
                        className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2.5 rounded-xl text-sm border border-blue-400/20 transition-all flex items-center justify-center gap-1 shadow-sm"
                      >
                        Billing Review <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* STEP 4: BILLING CYCLE & FINAL CONFIRMATION */}
                {assistantStep === 4 && (
                  <div className="space-y-4 animate-fadeIn">
                    <p className="text-sm text-slate-700">
                      "Please review the invoice scheduling and final details before authorizing the license activation."
                    </p>

                    {/* Summary Statement */}
                    <div className="bg-slate-50 border border-gray-200 rounded-2xl p-4 space-y-3 font-mono text-xs text-slate-700">
                      <div className="flex justify-between border-b border-gray-100 pb-2">
                        <span className="text-slate-400 font-sans font-bold">CLIENT ADMIN:</span>
                        <span className="text-slate-900 font-bold">{clients.find(c => c.id === assistantClient)?.name}</span>
                      </div>
                      <div className="flex justify-between border-b border-gray-100 pb-2">
                        <span className="text-slate-400 font-sans font-bold">RETAIL SHOP:</span>
                        <span className="text-slate-900 font-bold">{clients.find(c => c.id === assistantClient)?.shopName}</span>
                      </div>
                      <div className="flex justify-between border-b border-gray-100 pb-2">
                        <span className="text-slate-400 font-sans font-bold">BILLING TIER:</span>
                        <span className="text-blue-600 font-bold">{assistantPlan} Subscription</span>
                      </div>
                      <div className="flex justify-between border-b border-gray-100 pb-2">
                        <span className="text-slate-400 font-sans font-bold">BILLING CYCLE:</span>
                        <span className="text-slate-800 font-bold">{(plans[assistantPlan] || plans.Starter).cycle}</span>
                      </div>
                      <div className="flex justify-between border-b border-gray-100 pb-2">
                        <span className="text-slate-400 font-sans font-bold">NEXT DUE DATE:</span>
                        <span className="text-slate-800 font-bold font-mono">
                          {(() => {
                            const d = new Date();
                            d.setDate(d.getDate() + 30);
                            return d.toISOString().split('T')[0];
                          })()}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm pt-1">
                        <span className="text-emerald-600 font-bold font-sans">BILLING CHARGE:</span>
                        <span className="text-emerald-600 font-extrabold font-mono">₹{(plans[assistantPlan] || plans.Starter).price} INR</span>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex gap-2.5 items-start">
                      <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                      <p className="text-[10px] text-blue-800 leading-normal">
                        <strong>Super Admin Notification:</strong> Confirming this subscription broadcasts this contract directly to the Super Admin's financial ledger.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <button
                        onClick={() => setAssistantStep(3)}
                        className="bg-gray-100 hover:bg-gray-200 text-slate-700 font-bold py-2.5 rounded-xl text-sm transition-all"
                      >
                        Back
                      </button>
                      <button
                        onClick={handleExecuteAssignment}
                        disabled={isProcessingAssignment}
                        className="bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white font-extrabold py-2.5 rounded-xl text-sm border border-emerald-400/20 transition-all flex items-center justify-center gap-2 shadow-sm"
                      >
                        {isProcessingAssignment ? (
                          <>
                            <div className="spinner w-4 h-4" /> Activating...
                          </>
                        ) : (
                          <>
                            Confirm & Assign Plan
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* STEP 5: ASSIGNMENT SUMMARY */}
                {assistantStep === 5 && (
                  <div className="space-y-4 animate-fadeIn">
                    {/* Success Icon */}
                    <div className="flex flex-col items-center justify-center text-center py-2">
                      <div className="w-12 h-12 bg-emerald-50 border border-emerald-200 rounded-full flex items-center justify-center text-emerald-600 mb-2">
                        <Check className="w-7 h-7" />
                      </div>
                      <h4 className="font-extrabold text-slate-800 text-lg">Plan Assigned Successfully!</h4>
                      <p className="text-xs text-emerald-600 mt-1 font-semibold">Super Admin dashboard has been updated.</p>
                    </div>

                    <div className="bg-slate-50 border border-gray-150 rounded-2xl p-4 space-y-2.5 text-xs text-slate-700">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Client Shop:</span>
                        <span className="text-slate-800 font-bold">{clients.find(c => c.id === assistantClient)?.shopName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Assigned Plan:</span>
                        <span className="text-blue-600 font-bold font-mono">{assistantPlan}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Billing Cycle:</span>
                        <span className="text-slate-700">{PLANS[assistantPlan].cycle}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Next Billing Date:</span>
                        <span className="text-slate-800 font-mono font-bold">{clients.find(c => c.id === assistantClient)?.nextBilling}</span>
                      </div>
                    </div>

                    {/* Active features scroll preview */}
                    <div className="bg-slate-100 p-3 rounded-xl border border-gray-200">
                      <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-2">Activated Shop Features:</p>
                      <div className="max-h-[80px] overflow-y-auto space-y-1.5">
                        {PLANS[assistantPlan].features.map((feat, i) => (
                          <div key={i} className="flex items-center gap-2 text-[10px] text-slate-600">
                            <Check className="w-3 h-3 text-emerald-600 shrink-0" />
                            <span className="truncate">{feat}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={resetAssistant}
                      className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-xl text-sm transition-all shadow-sm"
                    >
                      Assign Another Client Plan
                    </button>
                  </div>
                )}

              </div>

              {/* Progress Tracker dots */}
              {assistantStep < 5 && (
                <div className="flex items-center justify-center gap-2 border-t border-gray-100 pt-4 mt-6">
                  {[1, 2, 3, 4].map(step => (
                    <div
                      key={step}
                      onClick={() => {
                        if (step < assistantStep) setAssistantStep(step);
                      }}
                      className={`h-2 rounded-full cursor-pointer transition-all duration-300 ${
                        step === assistantStep 
                          ? 'w-6 bg-blue-500' 
                          : step < assistantStep 
                            ? 'w-2 bg-emerald-500' 
                            : 'w-2 bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              )}

            </div>
          </div>

        </div>
      )}

      {/* -------------------------------------------------------------------------------------------------------------------------------- */}
      {/* 3. CLIENT ADMIN DASHBOARD SIMULATOR */}
      {/* -------------------------------------------------------------------------------------------------------------------------------- */}
      {activeRole === 'client' && (
        <div className="space-y-6">
          
          {/* Active client summary strip */}
          <div className="bg-white border border-blue-100 rounded-2xl p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-slate-900">{activeClient?.shopName} Admin Portal</h2>
                <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border ${
                  activeClient?.plan === 'Enterprise' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                  activeClient?.plan === 'Growth' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                  activeClient?.plan === 'Starter' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                  'bg-gray-100 text-gray-500 border border-gray-200'
                }`}>
                  Subscription Tier: {activeClient?.plan}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1 font-semibold">
                Owner: {activeClient?.name} | Phone: {activeClient?.phone} | Distributor Channel: {
                  distributors.find(d => d.id === activeClient?.distributorId)?.name
                }
              </p>
            </div>

            <div className="flex items-center gap-3">
              {activeClient?.nextBilling ? (
                <div className="bg-slate-50 px-4 py-2.5 rounded-xl border border-gray-200 flex items-center gap-3 text-sm">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider leading-none">Subscription Active</p>
                    <p className="text-xs text-slate-800 font-bold font-mono mt-1">Next Billing: {activeClient?.nextBilling}</p>
                  </div>
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 px-4 py-2.5 rounded-xl text-red-700 flex items-center gap-3 text-sm">
                  <ShieldAlert className="w-5 h-5" />
                  <div>
                    <p className="text-[10px] text-red-500 font-bold uppercase tracking-wider leading-none">Account Inactive</p>
                    <p className="text-xs font-semibold mt-1">No Active Plan Assigned</p>
                  </div>
                </div>
              )}
              <button
                onClick={() => setShowClientProfile(!showClientProfile)}
                className="bg-blue-650 hover:bg-blue-550 text-white font-extrabold px-4 py-2.5 rounded-xl text-xs transition-all border border-blue-400/20 shadow-sm flex items-center gap-1.5"
              >
                <Users className="w-4 h-4" /> {showClientProfile ? 'Hide Profile' : 'View Profile Details'}
              </button>
            </div>
          </div>

          {/* CLIENT PLAN & ACCOUNT PROFILE CARD */}
          {showClientProfile && (
            <div className="bg-gradient-to-br from-slate-900 to-slate-850 text-white border border-slate-800 rounded-3xl p-6 shadow-xl animate-fadeIn space-y-6 relative overflow-hidden">
              <div className="absolute right-0 top-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute left-0 bottom-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
              
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-slate-800 pb-4 gap-4">
                <div>
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-blue-400 animate-pulse" /> SaaS Account Subscription Profile
                  </h3>
                  <p className="text-slate-400 text-xs mt-0.5">Real-time subscription metadata & assigned tenant specifications</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="bg-blue-500/20 text-blue-400 text-xs font-extrabold px-3 py-1 rounded-full border border-blue-500/30 font-mono">
                    Plan: {activeClient?.plan || 'Starter'}
                  </span>
                  <span className={`text-xs font-extrabold px-3 py-1 rounded-full border ${
                    activeClient?.nextBilling ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'
                  }`}>
                    Status: {activeClient?.nextBilling ? 'ACTIVE' : 'LOCKED'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* 1. Account Metadata */}
                <div className="bg-slate-800/40 p-5 rounded-2xl border border-slate-700/50 space-y-3">
                  <h4 className="text-xs text-blue-400 font-extrabold uppercase tracking-wider">Business Info</h4>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Shop Admin:</span>
                      <span className="font-bold text-slate-200">{activeClient?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Company Shop:</span>
                      <span className="font-bold text-slate-200">{activeClient?.shopName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Contact Phone:</span>
                      <span className="font-bold text-slate-200 font-mono">{activeClient?.phone}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Billing Email:</span>
                      <span className="font-bold text-slate-200 font-mono">{activeClient?.email || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* 2. Subscription Details */}
                <div className="bg-slate-800/40 p-5 rounded-2xl border border-slate-700/50 space-y-3">
                  <h4 className="text-xs text-blue-400 font-extrabold uppercase tracking-wider">Billing Details</h4>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Assigned Plan:</span>
                      <span className="font-bold text-slate-200">{activeClient?.plan || 'Starter'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Recurring Price:</span>
                      <span className="font-extrabold text-emerald-450 font-mono">
                        ₹{plans[activeClient?.plan || 'Starter']?.price || '49'} INR/mo
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Activation Date:</span>
                      <span className="font-bold text-slate-200 font-mono">{activeClient?.assignedDate || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Billing Cycle:</span>
                      <span className="font-bold text-slate-200 font-mono">Monthly Recurring</span>
                    </div>
                  </div>
                </div>

                {/* 3. Managed Context */}
                <div className="bg-slate-800/40 p-5 rounded-2xl border border-slate-700/50 space-y-3">
                  <h4 className="text-xs text-blue-400 font-extrabold uppercase tracking-wider">Managed Channel</h4>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Distributor ID:</span>
                      <span className="font-bold text-slate-200 font-mono">{activeClient?.distributorId || 'Direct'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Distributor Partner:</span>
                      <span className="font-bold text-slate-200">
                        {distributors.find(d => d.id === activeClient?.distributorId)?.name || 'Direct Online Signup'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Billing Expiration:</span>
                      <span className="font-extrabold text-blue-400 font-mono">{activeClient?.nextBilling || 'Locked'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Unlocked plan features bar */}
              <div className="bg-slate-800/30 p-4 rounded-2xl border border-slate-850">
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-3">Activated SaaS Plan Feature Set:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {(plans[activeClient?.plan || 'Starter'] || plans.Starter).features.map((feat, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-300">
                      <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span className="truncate">{feat}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Feature Matrix & Modules Simulator */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            
            {/* MODULE 1: REPAIR JOBS (Starter, Growth, Enterprise) */}
            <div 
              onClick={() => {
                toast.success('Access Granted! Standard Repair Job module open.');
              }}
              className="bg-white hover:bg-blue-50/10 border border-blue-100 hover:border-blue-200 rounded-2xl p-5 shadow-sm cursor-pointer transition-all duration-200 group hover:-translate-y-1 relative"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="w-10 h-10 rounded-xl bg-blue-100 border border-blue-200 flex items-center justify-center text-blue-600">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <span className="bg-emerald-50 text-emerald-700 text-[10px] px-2 py-0.5 rounded font-extrabold uppercase border border-emerald-200">
                  Unlocked
                </span>
              </div>
              <h3 className="font-bold text-slate-800 group-hover:text-blue-600 transition-colors">Repair Job Management</h3>
              <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                Log repair work orders, track devices, print tickets, select technicians, and send automatic status alerts.
              </p>
              <div className="mt-4 pt-3 border-t border-gray-100 text-[10px] text-slate-400 font-mono">
                Standard in: Starter, Growth, Enterprise
              </div>
            </div>

            {/* MODULE 2: INVENTORY MANAGEMENT (Growth, Enterprise) */}
            {(() => {
              const currentPlanObj = plans[activeClient?.plan || 'Starter'] || plans.Starter;
              const isUnlocked = currentPlanObj.allowedFeatures 
                ? currentPlanObj.allowedFeatures.includes('Inventory')
                : (activeClient?.plan === 'Growth' || activeClient?.plan === 'Enterprise');
              return (
                <div 
                  onClick={() => {
                    if (isUnlocked) {
                      toast.success('Access Granted! Inventory module open.');
                    } else {
                      toast.error('Locked Module! Please contact your distributor to upgrade to Growth or Enterprise.');
                    }
                  }}
                  className={`border rounded-2xl p-5 shadow-sm cursor-pointer transition-all duration-200 group hover:-translate-y-1 relative ${
                    isUnlocked 
                      ? 'bg-white hover:bg-blue-50/10 border-blue-100 hover:border-blue-200' 
                      : 'bg-gray-50 border-gray-200 opacity-60'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      isUnlocked ? 'bg-blue-100 border border-blue-200 text-blue-600' : 'bg-gray-200 text-gray-400'
                    }`}>
                      {isUnlocked ? <CheckCircle2 className="w-6 h-6" /> : <Lock className="w-5 h-5 text-indigo-600" />}
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded font-extrabold uppercase border ${
                      isUnlocked ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-indigo-50 text-indigo-700 border-indigo-200'
                    }`}>
                      {isUnlocked ? 'Unlocked' : 'Requires Growth'}
                    </span>
                  </div>
                  <h3 className={`font-bold transition-colors ${isUnlocked ? 'text-slate-800 group-hover:text-blue-600' : 'text-slate-400'}`}>
                    Advanced Inventory Control
                  </h3>
                  <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                    Track hardware spare parts stock levels, monitor min-stock boundaries, manage suppliers, and log transactions.
                  </p>
                  <div className="mt-4 pt-3 border-t border-gray-100 text-[10px] text-slate-400 font-mono">
                    Standard in: Growth, Enterprise
                  </div>
                </div>
              );
            })()}

            {/* MODULE 3: EXPENSE & LEDGERS (Growth, Enterprise) */}
            {(() => {
              const currentPlanObj = plans[activeClient?.plan || 'Starter'] || plans.Starter;
              const isUnlocked = currentPlanObj.allowedFeatures 
                ? currentPlanObj.allowedFeatures.includes('Expenses')
                : (activeClient?.plan === 'Growth' || activeClient?.plan === 'Enterprise');
              return (
                <div 
                  onClick={() => {
                    if (isUnlocked) {
                      toast.success('Access Granted! Ledger module open.');
                    } else {
                      toast.error('Locked Module! Please contact your distributor to upgrade to Growth or Enterprise.');
                    }
                  }}
                  className={`border rounded-2xl p-5 shadow-sm cursor-pointer transition-all duration-200 group hover:-translate-y-1 relative ${
                    isUnlocked 
                      ? 'bg-white hover:bg-blue-50/10 border-blue-100 hover:border-blue-200' 
                      : 'bg-gray-50 border-gray-200 opacity-60'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      isUnlocked ? 'bg-blue-100 border border-blue-200 text-blue-600' : 'bg-gray-200 text-gray-400'
                    }`}>
                      {isUnlocked ? <CheckCircle2 className="w-6 h-6" /> : <Lock className="w-5 h-5 text-indigo-600" />}
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded font-extrabold uppercase border ${
                      isUnlocked ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-indigo-50 text-indigo-700 border-indigo-200'
                    }`}>
                      {isUnlocked ? 'Unlocked' : 'Requires Growth'}
                    </span>
                  </div>
                  <h3 className={`font-bold transition-colors ${isUnlocked ? 'text-slate-800 group-hover:text-blue-600' : 'text-slate-400'}`}>
                    Expenses & Customer Ledger
                  </h3>
                  <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                    Account bookkeeping ledgers, track overhead operational expenses, record credits/debits, and export statements.
                  </p>
                  <div className="mt-4 pt-3 border-t border-gray-100 text-[10px] text-slate-400 font-mono">
                    Standard in: Growth, Enterprise
                  </div>
                </div>
              );
            })()}

            {/* MODULE 4: CLOUD SYNC & BARCODE PRINTING (Enterprise) */}
            {(() => {
              const currentPlanObj = plans[activeClient?.plan || 'Starter'] || plans.Starter;
              const isUnlocked = currentPlanObj.allowedFeatures 
                ? (currentPlanObj.allowedFeatures.includes('Settings') || currentPlanObj.allowedFeatures.includes('Products'))
                : (activeClient?.plan === 'Enterprise');
              return (
                <div 
                  onClick={() => {
                    if (isUnlocked) {
                      toast.success('Access Granted! Premium Enterprise module open.');
                    } else {
                      toast.error('Locked Module! Contact your distributor to upgrade your plan to Enterprise.');
                    }
                  }}
                  className={`border rounded-2xl p-5 shadow-sm cursor-pointer transition-all duration-200 group hover:-translate-y-1 relative ${
                    isUnlocked 
                      ? 'bg-white hover:bg-blue-50/10 border-blue-100 hover:border-blue-200' 
                      : 'bg-gray-50 border-gray-200 opacity-60'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      isUnlocked ? 'bg-blue-100 border border-blue-200 text-blue-600' : 'bg-gray-200 text-gray-400'
                    }`}>
                      {isUnlocked ? <CheckCircle2 className="w-6 h-6" /> : <Lock className="w-5 h-5 text-indigo-600" />}
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded font-extrabold uppercase border ${
                      isUnlocked ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-indigo-50 text-indigo-700 border-indigo-200'
                    }`}>
                      {isUnlocked ? 'Unlocked' : 'Requires Enterprise'}
                    </span>
                  </div>
                  <h3 className={`font-bold transition-colors ${isUnlocked ? 'text-slate-800 group-hover:text-blue-600' : 'text-slate-400'}`}>
                    Cloud Premium & Integrations
                  </h3>
                  <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                    Complete integration suite: premium Google Drive OAuth client backup, custom barcode formatting & labels.
                  </p>
                  <div className="mt-4 pt-3 border-t border-gray-100 text-[10px] text-slate-400 font-mono">
                    Standard in: Enterprise
                  </div>
                </div>
              );
            })()}

          </div>

          {/* Subscription Upgrade Help banner */}
          <div className="bg-white border border-blue-150 rounded-3xl p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden">
            <div className="absolute right-0 bottom-0 w-48 h-48 bg-blue-500/5 rounded-full blur-2xl pointer-events-none" />
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-100 border border-blue-200 flex items-center justify-center text-blue-600 shrink-0">
                <Settings className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-extrabold text-slate-900 text-base">Subscription Plan Upgrades</h3>
                <p className="text-xs text-slate-500 mt-1 max-w-xl font-semibold">
                  Client shops cannot change their own billing package to enforce pricing plans. If you require higher boundaries (e.g. multi-branch logging or premium cloud back-ups), please request an upgrade from your managing distributor.
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                const myDistName = distributors.find(d => d.id === activeClient?.distributorId)?.name || 'Distributor';
                toast(`Contact request mock sent to ${myDistName}!`, { icon: '✉️' });
              }}
              className="bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-bold px-5 py-2.5 rounded-xl text-sm transition-all shrink-0 border border-blue-400/20 shadow-sm"
            >
              Request Plan Upgrade
            </button>
          </div>
        </div>
      )}

      {/* REUSABLE PREMIUM GPAY / UPI CHEKOUT MODAL */}
      <RazorpayModal
        isOpen={isRazorpayOpen}
        onClose={() => setIsRazorpayOpen(false)}
        amountInINR={getPlanPriceINR(assistantPlan)}
        description={`SaaS subscription assignment - ${assistantPlan} Plan`}
        clientName={clients.find(c => c.id === assistantClient)?.name || ''}
        clientEmail={clients.find(c => c.id === assistantClient)?.email || ''}
        clientPhone={clients.find(c => c.id === assistantClient)?.phone || ''}
        onSuccess={() => {
          setIsRazorpayOpen(false);
          handleExecuteAssignment();
        }}
      />

      <PayPalModal
        isOpen={isPayPalOpen}
        onClose={() => setIsPayPalOpen(false)}
        amountInINR={getPlanPriceINR(assistantPlan)}
        description={`SaaS subscription assignment - ${assistantPlan} Plan`}
        onSuccess={() => {
          setIsPayPalOpen(false);
          handleExecuteAssignment();
        }}
      />

      {/* REUSABLE MANUAL SUBSCRIPTION RENEWAL MODAL (GPAY / UPI / CASH) */}
      {isRenewModalOpen && clientToRenew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-lg w-full overflow-hidden text-slate-800">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-extrabold text-base">Manual Account Renewal</h3>
                  <p className="text-xs text-emerald-100">GPay, UPI, Cash & Offline Subscription Extender</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsRenewModalOpen(false)}
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              {/* Client Info Card */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-1.5 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-slate-500 font-medium">Client Business:</span>
                  <strong className="text-slate-900 font-bold text-sm">{clientToRenew.shopName}</strong>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500 font-medium">Owner / Admin:</span>
                  <span className="text-slate-700 font-semibold">{clientToRenew.name} ({clientToRenew.phone})</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500 font-medium">Current SaaS Plan:</span>
                  <span className="font-bold text-blue-700 bg-blue-50 px-2.5 py-0.5 rounded-full border border-blue-200">
                    {clientToRenew.plan || 'Starter'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500 font-medium">Current Expiry / Billing:</span>
                  <span className="font-mono font-bold text-slate-700">
                    {clientToRenew.nextBilling ? new Date(clientToRenew.nextBilling).toLocaleDateString() : 'Expired / Not set'}
                  </span>
                </div>
              </div>

              {/* Renewal Duration Selector */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                  Select Renewal Duration
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { label: '1 Month', months: 1, sub: '+30 Days' },
                    { label: '3 Months', months: 3, sub: '+90 Days' },
                    { label: '6 Months', months: 6, sub: '+180 Days' },
                    { label: '1 Year', months: 12, sub: '+365 Days' }
                  ].map((tier) => (
                    <button
                      type="button"
                      key={tier.months}
                      onClick={() => setRenewMonths(tier.months)}
                      className={`p-2.5 rounded-xl border text-center transition-all ${
                        renewMonths === tier.months
                          ? 'border-emerald-500 bg-emerald-50/80 text-emerald-900 font-extrabold ring-2 ring-emerald-500 shadow-sm'
                          : 'border-slate-200 hover:border-emerald-200 text-slate-600 bg-white'
                      }`}
                    >
                      <div className="text-xs">{tier.label}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{tier.sub}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Payment Mode Selector */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                  Payment Method
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {['GPay / UPI', 'Cash', 'Bank Transfer'].map((method) => (
                    <button
                      type="button"
                      key={method}
                      onClick={() => setRenewPaymentMethod(method)}
                      className={`py-2 px-3 rounded-xl border text-xs font-bold transition-all text-center ${
                        renewPaymentMethod === method
                          ? 'border-emerald-600 bg-emerald-600 text-white shadow-sm'
                          : 'border-slate-200 hover:border-slate-300 text-slate-700 bg-white'
                      }`}
                    >
                      {method === 'GPay / UPI' ? '🟢 GPay / UPI' : method === 'Cash' ? '💵 Cash' : '🏦 Bank Transfer'}
                    </button>
                  ))}
                </div>
              </div>

              {/* GPay UTR / Transaction ID */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Transaction Reference / GPay UTR Number
                  <span className="text-slate-400 font-normal lowercase ml-1">(optional)</span>
                </label>
                <input
                  type="text"
                  value={renewUtr}
                  onChange={(e) => setRenewUtr(e.target.value)}
                  placeholder="e.g. 428901238910 or GPay Ref #"
                  className="w-full bg-white border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                />
              </div>

              {/* Notice Banner */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex gap-2.5 items-start">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <p className="text-[11px] text-emerald-800 leading-normal">
                  Confirming will immediately extend this shop's subscription, remove the <strong>"expiring soon"</strong> alert, and keep their portal fully active.
                </p>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsRenewModalOpen(false)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRenew}
                disabled={isSubmittingRenew}
                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white rounded-xl text-xs font-extrabold transition-all shadow-md flex items-center gap-2 disabled:opacity-50"
              >
                {isSubmittingRenew ? (
                  <>
                    <div className="spinner w-3.5 h-3.5" /> Renewing...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" /> Confirm & Renew Account
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global CSS animation for smooth transitions */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}} />
    </div>
  );
}
